import {
  Scene,
  Engine,
  Vector3,
  Color3,
  FreeCamera,
  HemisphericLight,
  MeshBuilder,
  StandardMaterial,
  Mesh,
  KeyboardEventTypes,
  InstancedMesh,
  Animation as BJSAnimation,
  Color4,
  PointerEventTypes,
  DynamicTexture
} from 'babylonjs';
import { connectToServer, setGameObjectFactory } from './sync';
import { BUILDING_DEFINITIONS, BuildingType, UnitType, UnitStance, UNIT_DEFINITIONS, WeaponStatsMap, SHOW_ALL_ORE } from '../shared/MessageType';
import { FogOfWarSystem } from './fogofwar';
import { UnitBuildingInfoPanel3D } from './unitBuildingInfoPanel3D';
import { Unit3D, Building3D, Ore3D } from './interfaces3d';
import { GameObjectFactory3D } from './GameObjectFactory';
import { createLowPolyTerrain } from './worldTerrain';

interface WorldCallbacks {
  callback: (id: number, socket: any) => void;
  tickCallback: (ticks: any) => void;
  terrainCallback: (terrain: any) => void;
  oresCallback: (ores: any) => void;
  resourcesCallback: (resources: any) => void;
  dataCallback: (data: any) => void;
  queueRender: (messageType: string, payload: any) => void;
  renderSparks: (sourcePlayerId: number, source: { x: number, y: number }, target: { x: number, y: number }) => void;
  renderAttack: (playerId: number, sourceUnit: any, attackedUnit: any) => void;
  processSyncQueue: () => void;
  queueConsoleMessage?: (message: string, color?: string) => void;
}

/**
 * Transforms world coordinates (x, y) to sector A1's local 3D coordinate system
 * @param worldX World X coordinate (0-10000)
 * @param worldY World Y coordinate (0-10000)
 * @param heightOffset Height offset from sector surface (default: 0)
 * @returns Vector3 position in sector A1's coordinate system
 */
function worldToSectorA1(
  worldX: number,
  worldY: number,
  sectorA1Center: Vector3,
  sectorA1Right: Vector3,
  sectorA1Up: Vector3,
  sectorA1Normal: Vector3,
  coordinateScale: number,
  worldSize: number,
  heightOffset: number = 0
): Vector3 {
  // Center world coordinates around origin (0,0 is at world center)
  const centeredX = (worldX - worldSize / 2) / coordinateScale;
  const centeredY = (worldY - worldSize / 2) / coordinateScale;

  // Transform to sector A1's local coordinate system
  // worldX maps to sector's right (local X), worldY maps to sector's up (local Z)
  const localOffset = sectorA1Right.scale(centeredX)
    .add(sectorA1Up.scale(centeredY))
    .add(sectorA1Normal.scale(heightOffset));

  return sectorA1Center.add(localOffset);
}

class Game3D implements WorldCallbacks {
  private canvas: HTMLCanvasElement;
  private engine: Engine;
  private scene: Scene;
  private socket: any = null;
  private worldPlayerId: number = -1;
  private gameState: any | null = null;
  private readonly WORLD_SIZE = 10000;
  private readonly COORDINATE_SCALE = 50; // Divide coordinates by this value
  private readonly GAME_FPS = 60;

  // Track debug helper objects
  private debugLines: { [id: string]: Mesh } = {};

  // Quadtree visualization
  private quadtreeMeshes: {
    units: Mesh[];
    buildings: Mesh[];
    ores: Mesh[];
  } = {
      units: [],
      buildings: [],
      ores: []
    };
  private quadtreeUpdateInterval: number | null = null;

  // Track game objects
  private serverUnits: { [playerId: number]: Unit3D[] } = {};
  private serverBuildings: { [playerId: number]: Building3D[] } = {};
  private otherPlayerRaces: { [playerId: number]: string } = {};
  private otherPlayerColours: { [playerId: number]: string } = {};
  private ores: Ore3D[] = [];
  private sparks: InstancedMesh[] = [];
  private rockets: Array<{ mesh: InstancedMesh, velocity: number, lifetime: number }> = [];
  private laserBeams: Array<{ mesh: Mesh, lifetime: number }> = [];
  private missiles: Array<{ mesh: InstancedMesh, velocity: Vector3, lifetime: number, targetX: number, targetY: number, targetZ: number }> = [];
  private orbitalMassDriverLastLaunch: { [key: string]: number } = {}; // Key: "playerId_id", Value: last launch timestamp
  private camera: FreeCamera;
  private fogOfWar: FogOfWarSystem | null = null;

  // Camera mode: 'free' for 6DOF movement, 'orbit' for orbiting around planet
  private cameraMode: 'free' | 'orbit' = 'free';

  // Orbit camera state
  private orbitAngle: number = 0; // Horizontal angle (azimuth) in radians
  private orbitElevation: number = Math.PI / 4; // Vertical angle (elevation) in radians, 45 degrees default
  private orbitRadius: number = 550; // Distance from planet center

  // Orbit camera velocities (for inertia)
  private orbitAngleVelocity: number = 0; // Angular velocity for horizontal rotation
  private orbitElevationVelocity: number = 0; // Angular velocity for vertical rotation
  private orbitRadiusVelocity: number = 0; // Velocity for radius changes

  // Camera offset angles (for looking at planet from different angles)
  private cameraOffsetPitch: number = 0; // Vertical offset angle (pitch) in radians
  private cameraOffsetYaw: number = 0; // Horizontal offset angle (yaw) in radians
  private cameraOffsetPitchVelocity: number = 0; // Velocity for pitch offset
  private cameraOffsetYawVelocity: number = 0; // Velocity for yaw offset

  // Mouse state for orbit camera panning
  private isMouseDown: boolean = false; // Left mouse button
  private isRightMouseDown: boolean = false; // Right mouse button
  private lastMouseX: number = 0;
  private lastMouseY: number = 0;
  private mouseDownX: number = 0;
  private mouseDownY: number = 0;
  private hasMouseMoved: boolean = false;

  // Saved free camera state (for when switching back from orbit)
  private savedFreeCameraPosition: Vector3 | null = null;
  private savedFreeCameraRotation: Vector3 | null = null;

  // Space layer
  private isInSpaceLayer: boolean = false;
  private groundCameraPosition: Vector3 | null = null;
  private groundCameraRotation: Vector3 | null = null;
  private spaceShips: Array<{
    mesh: Mesh;
    angle: number;
    radius: number;
    orbitalSpeed: number;
    centerX: number;
    centerY: number;
    centerZ: number;
  }> = [];

  // Range overlay system
  private showVisionRange: boolean = false;
  private showAttackRange: boolean = false;
  private showStanceIndicators: boolean = false;
  private showHealthbars: boolean = false;
  private visionRangeOverlays: { [key: string]: Mesh } = {};
  private attackRangeOverlays: { [key: string]: Mesh } = {};
  private stanceIndicators: { [key: string]: Mesh } = {};
  private guardPositionLines: { [key: string]: Mesh } = {};
  private healthbars: { [key: string]: { mesh: Mesh, texture: DynamicTexture } } = {};
  private sectorLabels: { [key: string]: { mesh: Mesh, texture: DynamicTexture } } = {};

  // Sector A1 coordinate system for transforming world coordinates
  private sectorA1Center: Vector3 | null = null;
  private sectorA1Normal: Vector3 | null = null; // Points from sector center toward planet center
  private sectorA1Right: Vector3 | null = null; // Local X axis
  private sectorA1Up: Vector3 | null = null; // Local Z axis

  private keyState = {
    w: false,
    s: false,
    a: false,
    d: false,
    x: false,
    c: false
  };

  // Base meshes for instancing
  private baseMeshes: {
    targetBeacon: Mesh;
    positionBeacon: Mesh;
    worker: Mesh;
    enemyWorker: Mesh;
    tank: Mesh;
    tankTurret: Mesh;
    tankBarrel: Mesh;
    enemyTank: Mesh;
    enemyTankTurret: Mesh;
    enemyTankBarrel: Mesh;
    artillery: Mesh;
    artilleryBarrel: Mesh;
    enemyArtillery: Mesh;
    enemyArtilleryBarrel: Mesh;
    crawler: Mesh;
    enemyCrawler: Mesh;
    scoutPlane: Mesh;
    enemyScoutPlane: Mesh;
    hub: Mesh;
    house: Mesh;
    cannon: Mesh;
    cannonBarrel: Mesh;
    laserCannon: Mesh;
    laserCannonBarrel: Mesh;
    missileCannon: Mesh;
    missileCannonBarrel: Mesh;
    oreProcessor: Mesh;
    ore: Mesh;
    wall: Mesh;
    solarFarm: Mesh;
    solarFarmPanel: Mesh;
    unitEconomy: Mesh;
    unitMilitary: Mesh;
    building: Mesh;
    spark: Mesh;
    sparkAttack: Mesh;
    rocket: Mesh;
    laserBeam: Mesh;
    missile: Mesh;
  } = {} as any;

  // Materials for different object types
  private materials: {
    targetBeacon: StandardMaterial;
    positionBeacon: StandardMaterial;
    worker: StandardMaterial;
    enemyWorker: StandardMaterial;
    tank: StandardMaterial;
    tankTurret: StandardMaterial;
    tankBarrel: StandardMaterial;
    enemyTank: StandardMaterial;
    enemyTankTurret: StandardMaterial;
    enemyTankBarrel: StandardMaterial;
    artillery: StandardMaterial;
    artilleryBarrel: StandardMaterial;
    enemyArtillery: StandardMaterial;
    enemyArtilleryBarrel: StandardMaterial;
    crawler: StandardMaterial;
    enemyCrawler: StandardMaterial;
    scoutPlane: StandardMaterial;
    enemyScoutPlane: StandardMaterial;
    hub: StandardMaterial;
    house: StandardMaterial;
    cannon: StandardMaterial;
    cannonBarrel: StandardMaterial;
    laserCannon: StandardMaterial;
    laserCannonBarrel: StandardMaterial;
    missileCannon: StandardMaterial;
    missileCannonBarrel: StandardMaterial;
    oreProcessor: StandardMaterial;
    ore: StandardMaterial;
    wall: StandardMaterial;
    solarFarm: StandardMaterial;
    solarFarmPanel: StandardMaterial;
    unitEconomy: StandardMaterial;
    unitMilitary: StandardMaterial;
    building: StandardMaterial;
    spark: StandardMaterial;
    sparkAttack: StandardMaterial;
    rocket: StandardMaterial;
    laserBeam: StandardMaterial;
    missile: StandardMaterial;
  } = {} as any;

  private animations: {
    oreAnimation: BJSAnimation;
  } = {} as any;

  private gameObjectFactory: GameObjectFactory3D | null = null;

  // Camera position caching
  private readonly CAMERA_POSITION_KEY = 'overwhelmed-camera-position';
  private readonly CAMERA_POSITION_EPSILON = 0.1; // Minimum movement threshold
  private readonly CAMERA_SAVE_THROTTLE_MS = 5000; // 5 seconds
  private lastSavedCameraPosition: Vector3 | null = null;
  private cameraSaveTimeout: number | null = null;

  // UI Panel
  private infoPanel: UnitBuildingInfoPanel3D | null = null;
  private selectedUnit: Unit3D | null = null;
  private selectedBuilding: Building3D | null = null;
  private star: Mesh;
  private stars: Mesh[] = [];
  private backgroundStars: Mesh[] = [];
  private backgroundStarDirections: Vector3[] = []; // Store direction vectors for background stars
  private satellites: Mesh[] = [];

  // Console message queue for batched updates
  private consoleMessageQueue: Array<{ message: string; color?: string }> = [];
  private consoleNeedsScroll: boolean = false;
  private planet: Mesh;

  constructor() {
    this.canvas = document.getElementById("renderCanvas") as HTMLCanvasElement;
    if (!this.canvas) throw new Error("Canvas element not found!");

    // Prevent right-click context menu on canvas (for orbit camera right-click panning)
    this.canvas.addEventListener('contextmenu', (e) => {
      if (this.cameraMode === 'orbit') {
        e.preventDefault();
      }
    });

    // Add mouse wheel support for zooming in orbit mode
    this.canvas.addEventListener('wheel', (e) => {
      if (this.cameraMode === 'orbit') {
        e.preventDefault();
        const wheelDelta = e.deltaY;
        const zoomSpeed = 1.0;
        const zoomAcceleration = wheelDelta > 0 ? zoomSpeed : -zoomSpeed;
        this.orbitRadiusVelocity += zoomAcceleration;
      }
    }, { passive: false });

    this.engine = new Engine(this.canvas, true);
    this.scene = this.createScene();

    // Scene background colour
    this.scene.clearColor = new Color4(0.1, 0.1, 0.1, 1);

    this.engine.runRenderLoop(() => {
      this.scene.render();
      this.updateUnitPositions(); // Smooth unit movement
      this.updateCameraPosition(); // Add camera movement update
      this.updateSparks();
      this.updateRockets(); // Update rocket animations
      this.checkAndLaunchRockets(); // Check if orbital mass drivers should launch rockets
      this.updateLaserBeams(); // Update laser beam animations
      this.updateMissiles(); // Update missile animations
      this.updateRangeOverlays(); // Update range overlays
      this.updateHealthbars(); // Update healthbars
      this.updateSelectedInstancePanel(); // Update panel if instance is selected
      this.processSyncQueue(); // Process sync queue incrementally
      this.updateRespawnButtonState(); // Update respawn button state
      this.updateSpaceShips(); // Update space ship orbital positions
      this.updateConsole(); // Process console message queue
      this.updateStars(); // Update star positions

      // Update fog of war
      if (this.fogOfWar && this.worldPlayerId >= 0) {
        const myUnits = this.serverUnits[this.worldPlayerId] || [];
        const myBuildings = this.serverBuildings[this.worldPlayerId] || [];

        this.fogOfWar.updateVisibility(myUnits, myBuildings, this.worldPlayerId);

        // Update enemy visibility
        Object.keys(this.serverUnits).forEach(playerId => {
          if (Number(playerId) !== this.worldPlayerId) {
            const enemyUnits = this.serverUnits[Number(playerId)] || [];
            const enemyBuildings = this.serverBuildings[Number(playerId)] || [];
            this.fogOfWar.updateEnemyVisibility(enemyUnits, enemyBuildings);
          }
        });
      }
    });

    window.addEventListener("resize", () => {
      this.engine.resize();
    });

    // Create animations for all game objects
    this.createAnimations();

    // Create base meshes for all game objects
    this.createBaseMeshes();

    // Create materials for all game objects
    this.createMaterials();

    // Initialize fog of war system
    this.fogOfWar = new FogOfWarSystem(
      this.scene,
      this.WORLD_SIZE,
      this.COORDINATE_SCALE
    );

    // Start quadtree visualization updates
    this.startQuadtreeVisualization();

    const that = this;

    const playerId = this.worldPlayerId;

    this.gameObjectFactory = {
      addExisting: (obj: any) => {
        console.log(`Adding existing object: ${obj}`);
      },
      getWorker: (world: any, x: any, y: any, fillColor: number, fillAlpha: number, playerId: number) => {
        console.log(`Getting worker at ${x}, ${y}`);
        let id = Math.floor(Math.random() * 1000000) + 20000;
        return that.handleUnitSpawn({ k: 1, x, y, playerId, id });
      },
      getTank: (world: any, k: any, x: any, y: any, fillColor: number, fillAlpha: number, playerId: number) => {
        console.log(`Getting tank at ${x}, ${y}`);
        let id = Math.floor(Math.random() * 1000000) + 20000;
        return that.handleUnitSpawn({ k: 2, x, y, playerId, id });
      },
      getCrawler: (world: any, x: any, y: any, fillColor: number, fillAlpha: number, playerId: number) => {
        console.log(`Getting crawler at ${x}, ${y}`);
        let id = Math.floor(Math.random() * 1000000) + 20000;
        return that.handleUnitSpawn({ k: 5, x, y, playerId, id });
      },
      getArtillery: (world: any, x: any, y: any, playerId: number) => {
        console.log(`Getting artillery at ${x}, ${y}`);
        let id = Math.floor(Math.random() * 1000000) + 20000;
        return that.handleUnitSpawn({ k: 9, x, y, playerId, id });
      },
      getScoutPlane: (world: any, x: any, y: any, playerId: number) => {
        console.log(`Getting scout plane at ${x}, ${y}`);
        let id = Math.floor(Math.random() * 1000000) + 20000;
        return that.handleUnitSpawn({ k: 10, x, y, playerId, id });
      },
      getHub: (world: any, x: any, y: any) => {
        console.log(`Getting hub at ${x}, ${y}`);
        const buildingInstance = this.baseMeshes.hub.createInstance("hub");
        const building = {
          mesh: buildingInstance,
          id: 0,
          playerId: 0,
          k: BuildingType.Hub,
          x,
          y,
          destroy: () => {
            buildingInstance.dispose();
          }
        } as Building3D;
        building.mesh.position = this.worldToSectorA1Position(x, y, 1);
        this.alignBuildingToPlanet(building.mesh);
        return building;
      },
      getHouse: (world: any, x: any, y: any) => {
        console.log(`Getting house at ${x}, ${y}`);
        const buildingInstance = this.baseMeshes.house.createInstance("house");
        const building = {
          mesh: buildingInstance,
          id: 0,
          playerId: 0,
          k: BuildingType.House,
          x,
          y,
          destroy: () => {
            buildingInstance.dispose();
          }
        } as Building3D;
        building.mesh.position = this.worldToSectorA1Position(x, y, 1);
        this.alignBuildingToPlanet(building.mesh);
        return building;
      },
      getCannon: (world: any, x: any, y: any) => {
        console.log(`Getting cannon at ${x}, ${y}`);
        const buildingInstance = this.baseMeshes.cannon.createInstance("cannon");
        const building = {
          mesh: buildingInstance,
          id: 0,
          playerId: 0,
          k: BuildingType.Cannon,
          x,
          y,
          destroy: () => {
            buildingInstance.dispose();
          }
        } as Building3D;
        building.mesh.position = this.worldToSectorA1Position(x, y, 1);
        this.alignBuildingToPlanet(building.mesh);

        const cannonBarrel = this.baseMeshes.cannonBarrel.createInstance("cannonBarrel");
        building.mesh.addChild(cannonBarrel);
        (building as any).cannonBarrel = cannonBarrel;
        (building as any).angle = Math.random() * 2 * Math.PI;
        // Position barrel so one end is at turret center, other end extends forward
        const barrelLength = 0.5; // Half the barrel length
        // It's sin for x and cos for z I guess
        const offsetX = Math.sin((building as any).angle) * barrelLength;
        const offsetZ = Math.cos((building as any).angle) * barrelLength;
        cannonBarrel.position = new Vector3(offsetX, 1.15, offsetZ);
        cannonBarrel.rotation = new Vector3(0, (building as any).angle + Math.PI / 2, 0);
        return building;
      },
      getLaserCannon: (world: any, x: any, y: any) => {
        console.log(`Getting laser cannon at ${x}, ${y}`);
        const buildingInstance = this.baseMeshes.laserCannon.createInstance("laserCannon");
        const building = {
          mesh: buildingInstance,
          id: 0,
          playerId: 0,
          k: BuildingType.LaserCannon,
          x,
          y,
          destroy: () => {
            buildingInstance.dispose();
          }
        } as Building3D;
        building.mesh.position = this.worldToSectorA1Position(x, y, 1);
        this.alignBuildingToPlanet(building.mesh);
        const laserBarrel = this.baseMeshes.laserCannonBarrel.createInstance("laserCannonBarrel");
        building.mesh.addChild(laserBarrel);
        (building as any).cannonBarrel = laserBarrel;
        (building as any).angle = Math.random() * 2 * Math.PI;
        // Position barrel so one end is at turret center, other end extends forward
        const barrelLength = 0.5; // Half the barrel length
        const offsetX = Math.sin((building as any).angle) * barrelLength;
        const offsetZ = Math.cos((building as any).angle) * barrelLength;
        laserBarrel.position = new Vector3(offsetX, 1.15, offsetZ);
        laserBarrel.rotation = new Vector3(0, (building as any).angle + Math.PI / 2, 0);
        return building;
      },
      getMissileCannon: (world: any, x: any, y: any) => {
        console.log(`Getting missile cannon at ${x}, ${y}`);
        const buildingInstance = this.baseMeshes.missileCannon.createInstance("missileCannon");
        const building = {
          mesh: buildingInstance,
          id: 0,
          playerId: 0,
          k: BuildingType.MissileCannon,
          x,
          y,
          destroy: () => {
            buildingInstance.dispose();
          }
        } as Building3D;
        building.mesh.position = this.worldToSectorA1Position(x, y, 1);
        this.alignBuildingToPlanet(building.mesh);
        const missileBarrel = this.baseMeshes.missileCannonBarrel.createInstance("missileCannonBarrel");
        building.mesh.addChild(missileBarrel);
        (building as any).cannonBarrel = missileBarrel;
        (building as any).angle = Math.random() * 2 * Math.PI;
        // Position barrel so one end is at turret center, other end extends forward
        const barrelLength = 0.5; // Half the barrel length
        const offsetX = Math.sin((building as any).angle) * barrelLength;
        const offsetZ = Math.cos((building as any).angle) * barrelLength;
        missileBarrel.position = new Vector3(offsetX, 1.15, offsetZ);
        missileBarrel.rotation = new Vector3(0, (building as any).angle + Math.PI / 2, 0);
        return building;
      },
      getOreProcessor: (world: any, x: any, y: any) => {
        console.log(`Getting ore processor at ${x}, ${y}`);
        const buildingInstance = this.baseMeshes.oreProcessor.createInstance("oreProcessor");
        const building = {
          mesh: buildingInstance,
          id: 0,
          playerId: 0,
          k: BuildingType.OreProcessor,
          x,
          y,
          destroy: () => {
            buildingInstance.dispose();
          }
        } as Building3D;
        building.mesh.position = this.worldToSectorA1Position(x, y, 1);
        this.alignBuildingToPlanet(building.mesh);
        return building;
      },
      getWall: (world: any, x: any, y: any) => {
        console.log(`Getting wall at ${x}, ${y}`);
        const buildingInstance = this.baseMeshes.wall.createInstance("wall");
        const building = {
          mesh: buildingInstance,
          id: 0,
          playerId: 0,
          k: BuildingType.Wall,
          x,
          y,
          destroy: () => {
            buildingInstance.dispose();
          }
        } as Building3D;
        building.mesh.position = this.worldToSectorA1Position(x, y, 1);
        this.alignBuildingToPlanet(building.mesh);
        return building;
      },
      getSolarFarm: (world: any, x: any, y: any) => {
        console.log(`Getting solar farm at ${x}, ${y}`);
        const buildingInstance = this.baseMeshes.solarFarm.createInstance("solarFarm");
        const building = {
          mesh: buildingInstance,
          id: 0,
          playerId: 0,
          k: BuildingType.SolarFarm,
          x,
          y,
          destroy: () => {
            buildingInstance.dispose();
          }
        } as Building3D;
        building.mesh.position = this.worldToSectorA1Position(x, y, 1);
        this.alignBuildingToPlanet(building.mesh);
        const solarFarmPanel = this.baseMeshes.solarFarmPanel.createInstance("solarFarmPanel");
        building.mesh.addChild(solarFarmPanel);
        (building as any).solarFarmPanel = solarFarmPanel;
        solarFarmPanel.position = new Vector3(0, 0.5, 0);
        solarFarmPanel.rotation = new Vector3(0, 0, 0);

        // Point to player camera
        solarFarmPanel.lookAt(this.star.position);
        solarFarmPanel.rotation.y += Math.PI;

        return building;
      },
    };

    // Set the game object factory
    setGameObjectFactory(this.gameObjectFactory);

    // Connect to server
    connectToServer(this);

    // Get file upload from user
    const file = document.getElementById('file') as HTMLInputElement;

    file.onchange = () => {
      const fileContent = file.files[0];
      if (fileContent) {
        const reader = new FileReader();
        reader.onload = (e) => {
          const code = e.target?.result as string;
          this.saveCodeToServer(code);
        };
        reader.readAsText(fileContent);
      }
    };


    // Hide hud logged-in-as, login and logout buttons
    const loggedInAs = document.getElementById('hud-logged-in-as-wrapper');
    if (loggedInAs) {
      loggedInAs.style.display = 'none';
    }
    const loginButton = document.getElementById('login');
    if (loginButton) {
      loginButton.style.display = 'none';
    }
    const logoutButton = document.getElementById('logout');
    if (logoutButton) {
      logoutButton.style.display = 'none';
    }

    // Set up code saving functionality
    const codeButton = document.getElementById('run');
    if (codeButton) {
      codeButton.onclick = () => this.saveCode();

      codeButton.style.display = 'none';
    }

    // Set up respawn button
    const respawnButton = document.getElementById('respawn');
    if (respawnButton) {
      respawnButton.onclick = () => this.handleRespawn();
      respawnButton.style.display = 'none';
    }

    const clearConsoleButton = document.getElementById('clearconsole');
    if (clearConsoleButton) {
      clearConsoleButton.onclick = () => {
        const console = document.getElementById('console');
        if (console) {
          console.innerHTML = '';
        }
      }
    }

    // Set up range overlay toggles
    const toggleVisionRangeButton = document.getElementById('toggle-vision-range');
    if (toggleVisionRangeButton) {
      toggleVisionRangeButton.onclick = () => {
        this.showVisionRange = !this.showVisionRange;
        this.updateRangeOverlays();
        // Toggle button appearance: cyan-700 when active, f0f0f0 when inactive
        toggleVisionRangeButton.style.backgroundColor = this.showVisionRange ? '#0891b2' : '#f0f0f0';
      };
    }

    const toggleAttackRangeButton = document.getElementById('toggle-attack-range');
    if (toggleAttackRangeButton) {
      toggleAttackRangeButton.onclick = () => {
        this.showAttackRange = !this.showAttackRange;
        this.updateRangeOverlays();
        // Toggle button appearance: cyan-700 when active, f0f0f0 when inactive
        toggleAttackRangeButton.style.backgroundColor = this.showAttackRange ? '#0891b2' : '#f0f0f0';
      };
    }

    const toggleStanceIndicatorsButton = document.getElementById('toggle-stance-indicators');
    if (toggleStanceIndicatorsButton) {
      toggleStanceIndicatorsButton.onclick = () => {
        this.showStanceIndicators = !this.showStanceIndicators;
        this.updateRangeOverlays();
        // Toggle button appearance: cyan-700 when active, f0f0f0 when inactive
        toggleStanceIndicatorsButton.style.backgroundColor = this.showStanceIndicators ? '#0891b2' : '#f0f0f0';
      };
    }

    const toggleHealthbarsButton = document.getElementById('toggle-healthbars');
    if (toggleHealthbarsButton) {
      toggleHealthbarsButton.onclick = () => {
        this.showHealthbars = !this.showHealthbars;
        this.updateHealthbars();
        // Toggle button appearance: cyan-700 when active, f0f0f0 when inactive
        toggleHealthbarsButton.style.backgroundColor = this.showHealthbars ? '#0891b2' : '#f0f0f0';
      };
    }

    // Set up camera mode toggle
    const toggleCameraModeButton = document.getElementById('toggle-camera-mode');
    if (toggleCameraModeButton) {
      toggleCameraModeButton.onclick = () => {
        this.toggleCameraMode();
        // Update button text
        const span = toggleCameraModeButton.querySelector('span');
        if (span) {
          span.textContent = this.cameraMode === 'orbit' ? 'Camera: Orbit' : 'Camera: Free';
        }
      };
    }

    // Load saved code if it exists
    const savedCode = localStorage.getItem('overwhelmed-code');
    if (savedCode) {
      const aceEditor = (window as any).editor;
      if (aceEditor) {
        aceEditor.setValue(savedCode);
        this.saveCode();
      }
    }

    // Load saved camera position
    this.loadCameraPosition();

    // Create unit/building info panel
    this.infoPanel = new UnitBuildingInfoPanel3D();

    // Set up click detection for units and buildings
    this.setupClickDetection();
  }

  private setupClickDetection(): void {
    this.scene.onPointerObservable.add((pointerInfo) => {
      // Handle mouse panning for orbit camera mode
      if (this.cameraMode === 'orbit') {
        if (pointerInfo.type === PointerEventTypes.POINTERDOWN) {
          // Check if it's a left mouse button (button 0)
          if (pointerInfo.event.button === 0) {
            this.isMouseDown = true;
            this.lastMouseX = pointerInfo.event.offsetX;
            this.lastMouseY = pointerInfo.event.offsetY;
            this.mouseDownX = pointerInfo.event.offsetX;
            this.mouseDownY = pointerInfo.event.offsetY;
            this.hasMouseMoved = false;
          }
          // Check if it's a right mouse button (button 2)
          else if (pointerInfo.event.button === 2) {
            this.isRightMouseDown = true;
            this.lastMouseX = pointerInfo.event.offsetX;
            this.lastMouseY = pointerInfo.event.offsetY;
          }
        } else if (pointerInfo.type === PointerEventTypes.POINTERUP) {
          if (pointerInfo.event.button === 0) {
            // If mouse didn't move much, treat it as a click
            const moveThreshold = 5; // pixels
            const totalMove = Math.sqrt(
              Math.pow(pointerInfo.event.offsetX - this.mouseDownX, 2) +
              Math.pow(pointerInfo.event.offsetY - this.mouseDownY, 2)
            );
            const wasClick = totalMove < moveThreshold;

            this.isMouseDown = false;

            // Process click if it wasn't a drag
            if (wasClick) {
              // Process click detection (code will continue below)
            } else {
              // Was a drag, skip click detection
              return;
            }
          } else if (pointerInfo.event.button === 2) {
            this.isRightMouseDown = false;
          }
        } else if (pointerInfo.type === PointerEventTypes.POINTERMOVE) {
          // Handle left mouse panning (orbit)
          if (this.isMouseDown) {
            // Handle mouse panning
            const deltaX = pointerInfo.event.offsetX - this.lastMouseX;
            const deltaY = pointerInfo.event.offsetY - this.lastMouseY;

            // Check if mouse has moved significantly
            const moveThreshold = 2; // pixels
            if (Math.abs(deltaX) > moveThreshold || Math.abs(deltaY) > moveThreshold) {
              this.hasMouseMoved = true;
            }

            if (this.hasMouseMoved) {
              const mouseSensitivity = 0.0001;
              this.orbitAngleVelocity += -deltaX * mouseSensitivity;
              this.orbitElevationVelocity += -deltaY * mouseSensitivity;
            }

            this.lastMouseX = pointerInfo.event.offsetX;
            this.lastMouseY = pointerInfo.event.offsetY;
          }

          // Handle right mouse panning (camera offset)
          if (this.isRightMouseDown) {
            const deltaX = pointerInfo.event.offsetX - this.lastMouseX;
            const deltaY = pointerInfo.event.offsetY - this.lastMouseY;

            const offsetSensitivity = 0.0005;
            this.cameraOffsetYawVelocity += deltaX * offsetSensitivity;
            this.cameraOffsetPitchVelocity -= deltaY * offsetSensitivity; // Negative because Y is inverted on screen

            this.lastMouseX = pointerInfo.event.offsetX;
            this.lastMouseY = pointerInfo.event.offsetY;
          }
        }
      }

      // Click detection
      // In orbit mode, handle clicks on mouse up (if it wasn't a drag)
      // In free mode, handle clicks on mouse down (original behavior)
      const shouldProcessClick =
        (pointerInfo.type === PointerEventTypes.POINTERDOWN && this.cameraMode !== 'orbit') ||
        (pointerInfo.type === PointerEventTypes.POINTERUP && this.cameraMode === 'orbit' && pointerInfo.event.button === 0);

      if (shouldProcessClick) {
        const pickResult = this.scene.pick(pointerInfo.event.offsetX, pointerInfo.event.offsetY);

        if (pickResult && pickResult.hit && pickResult.pickedMesh) {
          const clickedMesh = pickResult.pickedMesh;

          // Check if it's a unit or building
          let clickedUnit: Unit3D | null = null;
          let clickedBuilding: Building3D | null = null;

          // Helper to find the root unit mesh from any child mesh
          const findUnitFromMesh = (mesh: any): Unit3D | null => {
            let currentMesh = mesh;
            // Traverse up the parent chain to find the unit mesh
            while (currentMesh) {
              // Check if this mesh belongs to a unit
              for (const units of Object.values(this.serverUnits)) {
                const unit = units.find(u => {
                  return u.mesh === currentMesh ||
                    (u.turretMesh && u.turretMesh === currentMesh) ||
                    (u.barrelMesh && u.barrelMesh === currentMesh);
                });
                if (unit) {
                  return unit;
                }
              }
              currentMesh = currentMesh.parent;
            }
            return null;
          };

          // Search through all units
          clickedUnit = findUnitFromMesh(clickedMesh);

          // Helper to find the root building mesh from any child mesh
          const findBuildingFromMesh = (mesh: any): Building3D | null => {
            let currentMesh = mesh;
            // Traverse up the parent chain to find the building mesh
            while (currentMesh) {
              // Check if this mesh belongs to a building
              for (const buildings of Object.values(this.serverBuildings)) {
                const building = buildings.find(b => b.mesh === currentMesh);
                if (building) {
                  return building;
                }
              }
              currentMesh = currentMesh.parent;
            }
            return null;
          };

          // Search through all buildings
          if (!clickedUnit) {
            clickedBuilding = findBuildingFromMesh(clickedMesh);
          }

          // Update selection and panel
          if (clickedUnit) {
            this.selectedUnit = clickedUnit;
            this.selectedBuilding = null;
            if (this.infoPanel) {
              this.infoPanel.showUnitInstance(clickedUnit);
            }
          } else if (clickedBuilding) {
            this.selectedBuilding = clickedBuilding;
            this.selectedUnit = null;
            if (this.infoPanel) {
              this.infoPanel.showBuildingInstance(clickedBuilding);
            }
          } else {
            // Clicked on something else, deselect
            this.selectedUnit = null;
            this.selectedBuilding = null;
            if (this.infoPanel) {
              this.infoPanel.clearInstanceSelection();
            }
          }
        } else {
          // Clicked on empty space, deselect
          this.selectedUnit = null;
          this.selectedBuilding = null;
          if (this.infoPanel) {
            this.infoPanel.clearInstanceSelection();
          }
        }
      }
    });
  }

  // Create base meshes for all game objects
  private createBaseMeshes(): void {
    // Create base meshes for units
    this.baseMeshes.targetBeacon = MeshBuilder.CreateBox("targetBeacon_base", { width: 0.2, height: 2, depth: 0.2 }, this.scene);
    this.baseMeshes.positionBeacon = MeshBuilder.CreateBox("positionBeacon_base", { width: 0.2, height: 1, depth: 0.2 }, this.scene);
    this.baseMeshes.worker = MeshBuilder.CreateBox("worker_base", { width: 0.6, height: 0.6, depth: 0.4 }, this.scene);
    this.baseMeshes.enemyWorker = MeshBuilder.CreateBox("enemy_worker_base", { width: 0.6, height: 0.6, depth: 0.4 }, this.scene);
    this.baseMeshes.tank = MeshBuilder.CreateBox("tank_base", { width: 0.8, height: 0.5, depth: 0.8 }, this.scene);
    this.baseMeshes.tankTurret = MeshBuilder.CreateBox("tank_turret_base", { width: 0.5, height: 0.1, depth: 0.5 }, this.scene);
    this.baseMeshes.tankBarrel = MeshBuilder.CreateCylinder("tank_barrel_base", { height: 0.8, diameterTop: 0.1, diameterBottom: 0.1 }, this.scene);
    this.baseMeshes.enemyTank = MeshBuilder.CreateBox("enemy_tank_base", { width: 0.8, height: 0.5, depth: 0.8 }, this.scene);
    this.baseMeshes.enemyTankTurret = MeshBuilder.CreateBox("enemy_tank_turret_base", { width: 0.5, height: 0.1, depth: 0.5 }, this.scene);
    this.baseMeshes.enemyTankBarrel = MeshBuilder.CreateCylinder("enemy_tank_barrel_base", { height: 0.8, diameterTop: 0.1, diameterBottom: 0.1 }, this.scene);
    this.baseMeshes.artillery = MeshBuilder.CreateBox("artillery_base", { width: 0.6, height: 0.4, depth: 0.8 }, this.scene);
    this.baseMeshes.artilleryBarrel = MeshBuilder.CreateCylinder("artillery_barrel_base", { height: 1.2, diameterTop: 0.1, diameterBottom: 0.1 }, this.scene);
    this.baseMeshes.enemyArtillery = MeshBuilder.CreateBox("enemy_artillery_base", { width: 0.6, height: 0.4, depth: 0.8 }, this.scene);
    this.baseMeshes.enemyArtilleryBarrel = MeshBuilder.CreateCylinder("enemy_artillery_barrel_base", { height: 1.2, diameterTop: 0.1, diameterBottom: 0.1 }, this.scene);

    this.baseMeshes.crawler = MeshBuilder.CreateBox("crawler_base", { width: 0.6, height: 0.4, depth: 0.6 }, this.scene);
    this.baseMeshes.enemyCrawler = MeshBuilder.CreateBox("enemy_crawler_base", { width: 0.6, height: 0.4, depth: 0.6 }, this.scene);
    // Scout plane - simple airplane shape (box for body, box for wings)
    this.baseMeshes.scoutPlane = MeshBuilder.CreateBox("scoutPlane_base", { width: 0.8, height: 0.2, depth: 0.6 }, this.scene);
    this.baseMeshes.enemyScoutPlane = MeshBuilder.CreateBox("enemy_scoutPlane_base", { width: 0.8, height: 0.2, depth: 0.6 }, this.scene);

    this.baseMeshes.unitEconomy = MeshBuilder.CreateCylinder("unitEconomy_base", { height: 0.8, diameterTop: 0.5, diameterBottom: 0.6 }, this.scene);
    this.baseMeshes.unitMilitary = MeshBuilder.CreateCylinder("unitMilitary_base", { height: 1, diameterTop: 1, diameterBottom: 0.5 }, this.scene);

    // Create base meshes for buildings
    this.baseMeshes.hub = MeshBuilder.CreateBox("hub_base", { width: 4, height: 2, depth: 4 }, this.scene);
    this.baseMeshes.house = MeshBuilder.CreateBox("house_base", { width: 1, height: 1, depth: 1 }, this.scene);
    this.baseMeshes.cannon = MeshBuilder.CreateBox("cannon_base", { width: 1, height: 2, depth: 1 }, this.scene);
    this.baseMeshes.cannonBarrel = MeshBuilder.CreateBox("cannonBarrel_base", { width: 1, height: 0.3, depth: 0.3 }, this.scene);
    this.baseMeshes.laserCannon = MeshBuilder.CreateBox("laserCannon_base", { width: 1, height: 2, depth: 1 }, this.scene);
    this.baseMeshes.laserCannonBarrel = MeshBuilder.CreateCylinder("laserCannonBarrel_base", { height: 0.5, diameterTop: 0.2, diameterBottom: 0.2 }, this.scene);
    this.baseMeshes.missileCannon = MeshBuilder.CreateBox("missileCannon_base", { width: 1, height: 2, depth: 1 }, this.scene);
    this.baseMeshes.missileCannonBarrel = MeshBuilder.CreateBox("missileCannonBarrel_base", { width: 0.4, height: 0.6, depth: 0.4 }, this.scene);
    this.baseMeshes.oreProcessor = MeshBuilder.CreateBox("oreProcessor_base", { width: 1, height: 1, depth: 1 }, this.scene);
    this.baseMeshes.building = MeshBuilder.CreateBox("building_base", { width: 1, height: 1, depth: 1 }, this.scene);
    this.baseMeshes.spark = MeshBuilder.CreateBox("spark_base", { width: 1, height: 1, depth: 1 }, this.scene);
    this.baseMeshes.sparkAttack = MeshBuilder.CreateBox("sparkAttack_base", { width: 1, height: 1, depth: 1 }, this.scene);
    this.baseMeshes.rocket = MeshBuilder.CreateBox("rocket_base", { width: 0.3, height: 1.2, depth: 0.3 }, this.scene);
    this.baseMeshes.laserBeam = MeshBuilder.CreateCylinder("laserBeam_base", { height: 1, diameterTop: 0.1, diameterBottom: 0.1 }, this.scene);
    this.baseMeshes.missile = MeshBuilder.CreateBox("missile_base", { width: 0.2, height: 0.6, depth: 0.2 }, this.scene);

    // Create base mesh for ores
    this.baseMeshes.ore = MeshBuilder.CreateCylinder("ore_base", { height: 0.5, diameterTop: 0.5, diameterBottom: 2 }, this.scene);
    this.baseMeshes.ore.animations.push(this.animations.oreAnimation);

    // Create base mesh for walls
    this.baseMeshes.wall = MeshBuilder.CreateBox("wall_base", { width: 1, height: 0.5, depth: 1 }, this.scene);

    // Create base mesh for solar farms
    this.baseMeshes.solarFarm = MeshBuilder.CreateBox("solarFarm_base", { width: 0.5, height: 0.5, depth: 0.5 }, this.scene);

    this.baseMeshes.solarFarmPanel = MeshBuilder.CreateBox("solarFarmPanel_base", { width: 2, height: 0.2, depth: 2 }, this.scene);

    // Hide all base meshes
    Object.values(this.baseMeshes).forEach(mesh => {
      mesh.isVisible = false;
    });
  }

  // Create materials for all game objects
  private createMaterials(): void {
    // Create materials for units
    this.materials.worker = new StandardMaterial("workerMat", this.scene);
    this.materials.worker.diffuseColor = new Color3(0.2, 0.2, 0.6);

    this.materials.enemyWorker = new StandardMaterial("enemyWorkerMat", this.scene);
    this.materials.enemyWorker.diffuseColor = new Color3(0.6, 0.2, 0.2);

    this.materials.tank = new StandardMaterial("tankMat", this.scene);
    this.materials.tank.diffuseColor = new Color3(0.2, 0.2, 0.8);
    this.materials.tankTurret = new StandardMaterial("tankTurretMat", this.scene);
    this.materials.tankTurret.diffuseColor = new Color3(0.2, 0.2, 0.6);
    this.materials.tankBarrel = new StandardMaterial("tankBarrelMat", this.scene);
    this.materials.tankBarrel.diffuseColor = new Color3(0.2, 0.2, 0.4);

    this.materials.enemyTank = new StandardMaterial("enemyTankMat", this.scene);
    this.materials.enemyTank.diffuseColor = new Color3(0.8, 0.2, 0.2);
    this.materials.enemyTankTurret = new StandardMaterial("enemyTankTurretMat", this.scene);
    this.materials.enemyTankTurret.diffuseColor = new Color3(0.6, 0.2, 0.2);
    this.materials.enemyTankBarrel = new StandardMaterial("enemyTankBarrelMat", this.scene);
    this.materials.enemyTankBarrel.diffuseColor = new Color3(0.4, 0.2, 0.2);

    this.materials.artillery = new StandardMaterial("artilleryMat", this.scene);
    this.materials.artillery.diffuseColor = new Color3(0.2, 0.2, 0.8);
    this.materials.artilleryBarrel = new StandardMaterial("artilleryBarrelMat", this.scene);
    this.materials.artilleryBarrel.diffuseColor = new Color3(0.2, 0.2, 0.4);

    this.materials.enemyArtillery = new StandardMaterial("enemyArtilleryMat", this.scene);
    this.materials.enemyArtillery.diffuseColor = new Color3(0.8, 0.2, 0.2);
    this.materials.enemyArtilleryBarrel = new StandardMaterial("enemyArtilleryBarrelMat", this.scene);
    this.materials.enemyArtilleryBarrel.diffuseColor = new Color3(0.4, 0.2, 0.2);

    this.materials.crawler = new StandardMaterial("crawlerMat", this.scene);
    this.materials.crawler.diffuseColor = new Color3(0.2, 0.2, 0.8);
    this.materials.enemyCrawler = new StandardMaterial("enemyCrawlerMat", this.scene);
    this.materials.enemyCrawler.diffuseColor = new Color3(0.8, 0.2, 0.2);
    this.materials.scoutPlane = new StandardMaterial("scoutPlaneMat", this.scene);
    this.materials.scoutPlane.diffuseColor = new Color3(0.3, 0.5, 0.8);
    this.materials.enemyScoutPlane = new StandardMaterial("enemyScoutPlaneMat", this.scene);
    this.materials.enemyScoutPlane.diffuseColor = new Color3(0.8, 0.3, 0.3);

    this.materials.unitEconomy = new StandardMaterial("unitEconomyMat", this.scene);
    this.materials.unitEconomy.diffuseColor = new Color3(0.5, 0.5, 0.5);

    this.materials.unitMilitary = new StandardMaterial("unitMilitaryMat", this.scene);
    this.materials.unitMilitary.diffuseColor = new Color3(0.5, 0.5, 0.5);

    // Create materials for buildings
    this.materials.hub = new StandardMaterial("hubMat", this.scene);
    this.materials.hub.diffuseColor = new Color3(0.2, 0.6, 0.8);
    // Make hub slightly transparent to debug units being inside it
    this.materials.hub.alpha = 0.7;

    this.materials.house = new StandardMaterial("houseMat", this.scene);
    this.materials.house.diffuseColor = new Color3(0.6, 0.4, 0.2);

    this.materials.cannon = new StandardMaterial("cannonMat", this.scene);
    this.materials.cannon.diffuseColor = new Color3(0.4, 0.4, 0.4);

    this.materials.cannonBarrel = new StandardMaterial("cannonBarrelMat", this.scene);
    this.materials.cannonBarrel.diffuseColor = new Color3(0.4, 0.4, 0.4);

    this.materials.laserCannon = new StandardMaterial("laserCannonMat", this.scene);
    this.materials.laserCannon.diffuseColor = new Color3(0.2, 0.4, 0.8);

    this.materials.laserCannonBarrel = new StandardMaterial("laserCannonBarrelMat", this.scene);
    this.materials.laserCannonBarrel.diffuseColor = new Color3(0.1, 0.3, 0.9);
    this.materials.laserCannonBarrel.emissiveColor = new Color3(0.2, 0.4, 0.8);

    this.materials.missileCannon = new StandardMaterial("missileCannonMat", this.scene);
    this.materials.missileCannon.diffuseColor = new Color3(0.5, 0.3, 0.2);

    this.materials.missileCannonBarrel = new StandardMaterial("missileCannonBarrelMat", this.scene);
    this.materials.missileCannonBarrel.diffuseColor = new Color3(0.4, 0.2, 0.1);

    this.materials.oreProcessor = new StandardMaterial("oreProcessorMat", this.scene);
    this.materials.oreProcessor.diffuseColor = new Color3(0.8, 0.6, 0.2);

    this.materials.building = new StandardMaterial("buildingMat", this.scene);
    this.materials.building.diffuseColor = new Color3(0.5, 0.5, 0.5);

    // Create material for ores
    this.materials.ore = new StandardMaterial("oreMat", this.scene);
    this.materials.ore.diffuseColor = new Color3(0.8, 0.1, 0.1);
    this.materials.ore.specularColor = new Color3(0.5, 0.6, 0.87);
    this.materials.ore.alpha = 0.4;

    // Create material for walls
    this.materials.wall = new StandardMaterial("wallMat", this.scene);
    this.materials.wall.diffuseColor = new Color3(0.5, 0.5, 0.5);

    // Create material for solar farms
    this.materials.solarFarm = new StandardMaterial("solarFarmMat", this.scene);
    this.materials.solarFarm.diffuseColor = new Color3(0.5, 0.5, 0.5);

    this.materials.solarFarmPanel = new StandardMaterial("solarFarmPanelMat", this.scene);
    this.materials.solarFarmPanel.diffuseColor = new Color3(0.5, 0.5, 0.5);

    // Create material for sparks
    this.materials.spark = new StandardMaterial("sparkMat", this.scene);
    this.materials.spark.diffuseColor = new Color3(1, 1, 1);
    this.materials.spark.specularColor = new Color3(1, 1, 1);

    this.materials.sparkAttack = new StandardMaterial("sparkAttackMat", this.scene);
    this.materials.sparkAttack.diffuseColor = new Color3(1, 0, 0);
    this.materials.sparkAttack.specularColor = new Color3(1, 0, 0);

    this.materials.rocket = new StandardMaterial("rocketMat", this.scene);
    this.materials.rocket.diffuseColor = new Color3(0.8, 0.2, 0.2);
    this.materials.rocket.emissiveColor = new Color3(0.5, 0.1, 0.1);

    this.materials.laserBeam = new StandardMaterial("laserBeamMat", this.scene);
    this.materials.laserBeam.diffuseColor = new Color3(0.1, 0.3, 0.9);
    this.materials.laserBeam.emissiveColor = new Color3(0.2, 0.4, 1.0);
    this.materials.laserBeam.alpha = 0.8;

    this.materials.missile = new StandardMaterial("missileMat", this.scene);
    this.materials.missile.diffuseColor = new Color3(0.9, 0.7, 0.2);
    this.materials.missile.emissiveColor = new Color3(0.7, 0.5, 0.1);

    this.materials.targetBeacon = new StandardMaterial("targetBeaconMat", this.scene);
    this.materials.targetBeacon.diffuseColor = new Color3(0.2, 0.8, 0.8);
    this.materials.positionBeacon = new StandardMaterial("positionBeaconMat", this.scene);
    this.materials.positionBeacon.diffuseColor = new Color3(0.8, 0.8, 0.2);

    // Apply materials to base meshes
    this.baseMeshes.targetBeacon.material = this.materials.targetBeacon;
    this.baseMeshes.positionBeacon.material = this.materials.positionBeacon;
    this.baseMeshes.worker.material = this.materials.worker;
    this.baseMeshes.enemyWorker.material = this.materials.enemyWorker;
    this.baseMeshes.tank.material = this.materials.tank;
    this.baseMeshes.tankTurret.material = this.materials.tankTurret;
    this.baseMeshes.tankBarrel.material = this.materials.tankBarrel;
    this.baseMeshes.enemyTank.material = this.materials.enemyTank;
    this.baseMeshes.enemyTankTurret.material = this.materials.enemyTankTurret;
    this.baseMeshes.enemyTankBarrel.material = this.materials.enemyTankBarrel;
    this.baseMeshes.artillery.material = this.materials.artillery;
    this.baseMeshes.artilleryBarrel.material = this.materials.artilleryBarrel;
    this.baseMeshes.enemyArtillery.material = this.materials.enemyArtillery;
    this.baseMeshes.enemyArtilleryBarrel.material = this.materials.enemyArtilleryBarrel;
    this.baseMeshes.unitEconomy.material = this.materials.unitEconomy;
    this.baseMeshes.unitMilitary.material = this.materials.unitMilitary;
    this.baseMeshes.crawler.material = this.materials.crawler;
    this.baseMeshes.enemyCrawler.material = this.materials.enemyCrawler;
    this.baseMeshes.scoutPlane.material = this.materials.scoutPlane;
    this.baseMeshes.enemyScoutPlane.material = this.materials.enemyScoutPlane;
    this.baseMeshes.hub.material = this.materials.hub;
    this.baseMeshes.house.material = this.materials.house;
    this.baseMeshes.cannon.material = this.materials.cannon;
    this.baseMeshes.cannonBarrel.material = this.materials.cannonBarrel;
    this.baseMeshes.laserCannon.material = this.materials.laserCannon;
    this.baseMeshes.laserCannonBarrel.material = this.materials.laserCannonBarrel;
    this.baseMeshes.missileCannon.material = this.materials.missileCannon;
    this.baseMeshes.missileCannonBarrel.material = this.materials.missileCannonBarrel;
    this.baseMeshes.oreProcessor.material = this.materials.oreProcessor;
    this.baseMeshes.building.material = this.materials.building;
    this.baseMeshes.ore.material = this.materials.ore;
    this.baseMeshes.spark.material = this.materials.spark;
    this.baseMeshes.sparkAttack.material = this.materials.sparkAttack;
    this.baseMeshes.wall.material = this.materials.wall;
    this.baseMeshes.solarFarm.material = this.materials.solarFarm;
    this.baseMeshes.solarFarmPanel.material = this.materials.solarFarmPanel;
    this.baseMeshes.rocket.material = this.materials.rocket;
    this.baseMeshes.laserBeam.material = this.materials.laserBeam;
    this.baseMeshes.missile.material = this.materials.missile;
  }

  createAnimations() {
    // Create animation for ore
    this.animations.oreAnimation = new BJSAnimation("oreAnimation", "position.y",
      60,
      BJSAnimation.ANIMATIONTYPE_FLOAT,
      BJSAnimation.ANIMATIONLOOPMODE_YOYO,
    );
    this.animations.oreAnimation.setKeys([
      {
        frame: 0,
        value: 0.3
      },
      {
        frame: 60,
        value: 0.5
      }
    ]);
  }

  // WorldCallbacks implementation
  callback = (id: number, socket: any) => {
    this.worldPlayerId = id;
    this.socket = socket;
    console.log(`Connected to server as player ${id}`);
    socket.emit('getOres', id);
    // Show the code button
    const codeButton = document.getElementById('run');
    if (codeButton && this.worldPlayerId) {
      codeButton.style.display = 'inline-block';
    }
    // Show the respawn button
    const respawnButton = document.getElementById('respawn');
    if (respawnButton && this.worldPlayerId) {
      respawnButton.style.display = 'inline-block';
      this.updateRespawnButtonState();
    }
  };

  tickCallback = (ticks: any) => {
    // Handle game ticks
    console.log('Received tick update');
  };

  terrainCallback = (terrain: any) => {
    // Handle terrain updates
    console.log('Received terrain update');
  };

  oresCallback = (ores: any[]) => {
    // Remove ores that are no longer present
    const oresToRemove = this.ores.filter(
      existingOre => !ores.find(newOre => newOre.id === existingOre.id)
    );

    oresToRemove.forEach(ore => {
      ore.mesh.dispose();
      this.ores.splice(this.ores.indexOf(ore), 1);
    });

    let that = this;

    // Update or create ores
    ores.forEach(ore => {
      const existingOre = this.ores.find(o => o.id === ore.id);

      if (existingOre) {
        // Update existing ore
        existingOre.ore = ore.ore;
        // Scale the mesh based on remaining ore
        const scale = Math.max(0.3, ore.ore / 100);
        existingOre.mesh.scaling = new Vector3(scale, scale, scale);
        const animation = that.scene.beginAnimation(existingOre.mesh, 0, 60, true);
        const randomFrame = Math.floor(Math.random() * 60);
        animation.goToFrame(randomFrame);
      } else {
        // Create new ore instance from base mesh
        const oreInstance = this.baseMeshes.ore.createInstance("ore_" + ore.id);

        // Position slightly above ground and scale coordinates
        oreInstance.position = this.worldToSectorA1Position(ore.x, ore.y, 0.5);

        // Add to ores array
        this.ores.push({
          mesh: oreInstance,
          id: ore.id,
          x: ore.x / this.COORDINATE_SCALE,
          y: ore.y / this.COORDINATE_SCALE,
          ore: ore.ore
        });
      }
    });

    // Hide ores in fog of war
    if (this.fogOfWar) {
      this.ores.forEach(ore => {
        // Convert scaled coordinates back to world coordinates for fog check
        const worldX = ore.x * this.COORDINATE_SCALE;
        const worldY = ore.y * this.COORDINATE_SCALE;
        const visibility = this.fogOfWar.getVisibility(worldX, worldY);
        // Hide ores in unexplored areas
        ore.mesh.isVisible = visibility !== 0 || SHOW_ALL_ORE; // 0 = UNEXPLORED
      });
    }

    console.log(`Updated ores: ${this.ores.length} total`);
  };

  dataCallback = (data: any) => {
    const dataSizeElement = document.getElementById('hud-data-size');
    if (dataSizeElement) {
      dataSizeElement.textContent = data.length.toString() + " b";
    }
  };

  resourcesCallback = (resources: any) => {
    const { ore, metal, energy, energyLimit, control, computeTime } = resources;
    const oreElement = document.getElementById('hud-ore');
    const metalElement = document.getElementById('hud-metal');
    const metalPerMinElement = document.getElementById('hud-metal-permin');
    const energyElement = document.getElementById('hud-energy');
    const energyPerMinElement = document.getElementById('hud-energy-permin');
    const controlElement = document.getElementById('hud-control');
    const computeTimeElement = document.getElementById('hud-compute-time');
    if (oreElement) {
      oreElement.textContent = ore.toString();
    }

    // Calculate generation rates based on player's buildings
    let energyPerMinute = 0;
    let metalPerMinute = 0;

    if (this.worldPlayerId >= 0) {
      const myBuildings = this.serverBuildings[this.worldPlayerId] || [];
      const hubCount = myBuildings.filter(b => b.k === BuildingType.Hub).length;
      const solarFarmCount = myBuildings.filter(b => b.k === BuildingType.SolarFarm).length;
      const oreProcessorCount = myBuildings.filter(b => b.k === BuildingType.OreProcessor).length;

      // Energy generation: (hubCount + solarFarmCount) every 20 ticks = 4 seconds
      // Per minute: (hubCount + solarFarmCount) * (60/4) = (hubCount + solarFarmCount) * 15
      energyPerMinute = (hubCount + solarFarmCount) * 15;

      // Metal generation: min(hubCount + oreProcessorCount, playerOre) every 20 ticks
      // With TICK_INTERVAL = 200ms, 20 ticks = 4 seconds
      // Per minute: (hubCount + oreProcessorCount) * (60/4) = (hubCount + oreProcessorCount) * 15
      // But limited by available ore: playerOre * 15 per minute

      // Ore generation: 1 per hub every 5 ticks (when < 50)
      // With TICK_INTERVAL = 200ms, 5 ticks = 1 second
      // Per minute: hubCount * 60 per minute (but capped at 50 per hub storage)
      // Actual generation rate: hubCount * 12 per minute (1 per 5 ticks = 12 per minute per hub)

      const metalConversionCapacity = (hubCount + oreProcessorCount) * 15; // per minute
      const oreGenerationRate = hubCount * 12; // per minute (1 ore per hub per 5 ticks)

      // Metal generation is limited by:
      // 1. Conversion capacity (how much we can convert)
      // 2. Ore generation rate (how much ore we can produce)
      // 3. Available ore (if we're depleting faster than generating)

      if (ore > 0) {
        // The sustainable rate is the minimum of conversion capacity and ore generation
        const sustainableRate = Math.min(metalConversionCapacity, oreGenerationRate);

        // If we have enough ore to sustain the rate, use it
        // Otherwise, calculate based on how long current ore will last
        // Each conversion cycle (every 20 ticks = 4 seconds) consumes up to (hubCount + oreProcessorCount) ore
        const oreConsumptionPerCycle = hubCount + oreProcessorCount;
        const cyclesUntilDepletion = oreConsumptionPerCycle > 0 ? Math.floor(ore / oreConsumptionPerCycle) : Infinity;

        if (cyclesUntilDepletion > 10) {
          // Enough ore to sustain for a while, show sustainable rate
          metalPerMinute = sustainableRate;
        } else if (cyclesUntilDepletion > 0) {
          // Ore will deplete soon, show current rate but it will drop
          // Show the rate we can achieve with current ore
          metalPerMinute = Math.min(metalConversionCapacity, ore * 15); // ore * 15 = ore per minute conversion
        } else {
          // Very low ore, rate approaches 0
          metalPerMinute = 0;
        }
      } else {
        // No ore available, rate is 0
        metalPerMinute = 0;
      }
    }

    if (metalElement) {
      // Display metal value only (per-minute shown separately)
      metalElement.textContent = metal.toString();
    }
    if (metalPerMinElement) {
      // Display per-minute estimate separately, smaller and underneath
      metalPerMinElement.textContent = metalPerMinute > 0
        ? `+${Math.round(metalPerMinute)}/min`
        : '';
    }
    if (energyElement) {
      // Display energy value only (per-minute shown separately)
      if (energyLimit !== undefined) {
        energyElement.textContent = `${energy}/${energyLimit}`;
      } else {
        energyElement.textContent = energy.toString();
      }
    }
    if (energyPerMinElement) {
      // Display per-minute estimate separately, smaller and underneath
      energyPerMinElement.textContent = energyPerMinute > 0
        ? `+${Math.round(energyPerMinute)}/min`
        : '';
    }
    if (controlElement) {
      controlElement.textContent = control.toString();
    }
    if (computeTimeElement) {
      computeTimeElement.textContent = computeTime.toString() + "/500";
    }

    // Update game object count
    const gameObjectCountElement = document.getElementById('hud-game-object-count');
    if (gameObjectCountElement) {
      let countString = '';
      countString += `L${Object.keys(this.debugLines).length},`;
      countString += `S${this.sparks.length},`;
      countString += `O${this.ores.length},`;
      countString += `U${Object.values(this.serverUnits).reduce((acc, units) => acc + units.length, 0)},`;
      countString += `B${Object.values(this.serverBuildings).reduce((acc, buildings) => acc + buildings.length, 0)}`;
      gameObjectCountElement.textContent = countString;
    }
  };

  processSyncQueue = () => {
    // This will be overridden by connectToServer
    // Stub implementation to satisfy TypeScript
  };

  queueRender = (messageType: string, payload: any) => {
    // Handle different message types
    switch (messageType) {
      case 'spawnUnit':
      case 'spawnWorker':
      case 'spawnTank':
        this.handleUnitSpawn(payload);
        break;
      case 'move':
        this.handleUnitMove(payload);
        break;
      case 'spawnBuilding':
        this.handleBuildingSpawn(payload);
        break;
      // Add other message types as needed
    }
  };

  // Method to update unit/building life from sync data
  updateUnitLife(playerId: number, id: number, life: number) {
    const unit = this.serverUnits[playerId]?.find(u => u.id === id);
    if (unit) {
      unit.life = life;
      // Update panel if this unit is selected
      if (this.selectedUnit && this.selectedUnit.id === id && this.selectedUnit.playerId === playerId) {
        if (this.infoPanel) {
          this.infoPanel.showUnitInstance(unit);
        }
      }
    }
  }

  updateBuildingLife(playerId: number, id: number, life: number) {
    const building = this.serverBuildings[playerId]?.find(b => b.id === id);
    if (building) {
      building.life = life;
      // Update panel if this building is selected
      if (this.selectedBuilding && this.selectedBuilding.id === id && this.selectedBuilding.playerId === playerId) {
        if (this.infoPanel) {
          this.infoPanel.showBuildingInstance(building);
        }
      }
    }
  }

  renderAttack = (playerId: number, sourceUnit: any, attackedUnit: any) => {
    const sourceX = sourceUnit.x / this.COORDINATE_SCALE;
    const sourceY = sourceUnit.y / this.COORDINATE_SCALE;
    const sourceK = sourceUnit.k;
    const attackedX = attackedUnit.x / this.COORDINATE_SCALE;
    const attackedY = attackedUnit.y / this.COORDINATE_SCALE;
    const attackedK = attackedUnit.k;

    // Check if this is a building attack and determine weapon type
    const building = this.serverBuildings[playerId]?.find(b => b.id === sourceUnit.id);
    if (building) {
      if (building.k === BuildingType.LaserCannon) {
        // Render laser beam
        this.renderLaserBeam(sourceX, sourceY, attackedX, attackedY);
      } else if (building.k === BuildingType.MissileCannon) {
        // Render missile
        this.renderMissile(sourceX, sourceY, attackedX, attackedY);
      } else {
        // Default to sparks for other cannons
        this.renderSparks(playerId, { x: sourceX, y: sourceY, k: sourceK }, { x: attackedX, y: attackedY, k: attackedK }, true);
      }

      // Rotate cannon barrel to target
      if (building.k === BuildingType.Cannon || building.k === BuildingType.LaserCannon || building.k === BuildingType.MissileCannon) {
        // Calculate angle in sector A1's coordinate space
        const worldDx = attackedX - sourceX;
        const worldDy = attackedY - sourceY;
        let angle = this.calculateSectorA1Rotation(worldDx, worldDy);
        const cannonBarrel = (building as any).cannonBarrel;
        if (cannonBarrel) {
          // It's sin for x and cos for z I guess
          const offsetX = Math.sin(angle) * 0.5;
          const offsetZ = Math.cos(angle) * 0.5;
          cannonBarrel.position = new Vector3(offsetX, 1.15, offsetZ);
          // Rotation is relative to the building's base rotation (which aligns with sector A1)
          cannonBarrel.rotation = new Vector3(0, angle + Math.PI / 2, 0);
        }
      }
    } else {
      // Unit attack - use sparks
      this.renderSparks(playerId, { x: sourceX, y: sourceY, k: sourceK }, { x: attackedX, y: attackedY, k: attackedK }, true);
    }
  };

  renderSparks = (sourcePlayerId: number, source: { x: number, y: number, k: number }, target: { x: number, y: number, k: number }, isAttack: boolean = false) => {
    const { x, y, k: sk } = source;
    const { x: tx, y: ty, k: tk } = target;
    const distance = Math.sqrt((x - tx) * (x - tx) + (y - ty) * (y - ty));
    const sparkCount = isAttack ? 3 : 10;
    const idOffset = this.sparks.length;
    for (let i = 0; i < sparkCount; i++) {
      // Spred out sparks around the target
      const proportion = i / sparkCount;
      let angle = proportion * Math.PI * 2;
      angle += -Math.PI / 8 + Math.random() * Math.PI / 4;
      let spawnX = x;
      let spawnY = y;
      let sparkTargetX = tx;
      let sparkTargetY = ty;
      if (isAttack) {
        spawnX = x;
        spawnY = y;
      } else {
        spawnX = x + Math.cos(angle) * 10;
        spawnY = y + Math.sin(angle) * 10;
        sparkTargetX = tx + Math.cos(angle) * 10;
        sparkTargetY = ty + Math.sin(angle) * 10;
      }

      const sparkId = idOffset + i;
      const spark = isAttack ? this.baseMeshes.sparkAttack.createInstance("sparkAttack_" + sparkId) : this.baseMeshes.spark.createInstance("spark_" + sparkId);
      const heightOffset = sk === UnitType.ScoutPlane ? 8 : 1;
      const targetHeightOffset = tk === UnitType.ScoutPlane ? 8 : 1;
      spark.position = this.worldToSectorA1Position(spawnX, spawnY, heightOffset);
      const targetPos = this.worldToSectorA1Position(sparkTargetX, sparkTargetY, targetHeightOffset);
      spark.scaling = new Vector3(0.2, 0.2, 0.2);
      spark.material = isAttack ? this.materials.sparkAttack : this.materials.spark;
      (spark as any).isAttack = isAttack;
      (spark as any).targetPosition = targetPos; // Store transformed target position
      (spark as any).sk = sk;
      (spark as any).tk = tk;
      (spark as any).lifetimeTicks = 600;
      (spark as any).velocity = 20 - 0.5 * i;
      this.sparks.push(spark);
    }
  };

  private handleUnitSpawn(payload: any) {
    const { playerId, id, k, x, y } = payload;
    if (!this.serverUnits[playerId]) {
      this.serverUnits[playerId] = [];
    }

    // Is this my unit or someone elses
    const isMyUnit = Number(playerId) === this.worldPlayerId;

    // Create unit instance from base mesh
    let unitInstance = null;
    if (k === UnitType.Worker) {
      unitInstance = isMyUnit
        ? this.baseMeshes.worker.createInstance("unitEconomy_" + id)
        : this.baseMeshes.enemyWorker.createInstance("enemyWorker_" + id);
    } else if (k === UnitType.Tank) {
      if (isMyUnit) {
        unitInstance = this.baseMeshes.tank.createInstance("tank_" + id);
      } else {
        unitInstance = this.baseMeshes.enemyTank.createInstance("enemyTank_" + id);
      }
    } else if (k === UnitType.Crawler) {
      if (isMyUnit) {
        unitInstance = this.baseMeshes.crawler.createInstance("crawler_" + id);
      } else {
        unitInstance = this.baseMeshes.enemyCrawler.createInstance("enemyCrawler_" + id);
      }
    } else if (k === UnitType.Artillery) {
      unitInstance = isMyUnit
        ? this.baseMeshes.artillery.createInstance("artillery_" + id)
        : this.baseMeshes.enemyArtillery.createInstance("enemyArtillery_" + id);
    } else if (k === UnitType.ScoutPlane) {
      unitInstance = isMyUnit
        ? this.baseMeshes.scoutPlane.createInstance("scoutPlane_" + id)
        : this.baseMeshes.enemyScoutPlane.createInstance("enemyScoutPlane_" + id);
    } else {
      unitInstance = this.baseMeshes.unitMilitary.createInstance("unitMilitary_" + id);
    }
    // Air units are positioned higher
    const heightOffset = k === UnitType.ScoutPlane ? 8 : 0.5;
    unitInstance.position = this.worldToSectorA1Position(x, y, heightOffset);
    this.alignBuildingToPlanet(unitInstance);
    // Store base yaw for later rotation updates (so we can add target angle to it)
    (unitInstance as any).baseYaw = unitInstance.rotation.y;
    const that = this;
    const unit: Unit3D = {
      mesh: unitInstance,
      id,
      playerId,
      k,
      x,
      y,
      tx: x,
      ty: y,
      vx: 0,
      vy: 0,
      currentFacing: 0, // Initialize facing direction
      life: payload.life !== undefined ? payload.life : UNIT_DEFINITIONS[k].life, // Initialize life from payload or default
      init: () => { },
      destroy: () => {
        unitInstance.dispose();
        if (unitInstance.getChildMeshes().length > 0) {
          unitInstance.getChildMeshes().forEach(child => {
            child.dispose();
          });
        }
        if ((unit as any).targetBeacon) {
          (unit as any).targetBeacon.dispose();
          delete (unit as any).targetBeacon;
        }
        if ((unit as any).positionBeacon) {
          (unit as any).positionBeacon.dispose();
          delete (unit as any).positionBeacon;
        }
        if ((unit as any).turretMesh) {
          (unit as any).turretMesh.dispose();
          delete (unit as any).turretMesh;
        }
        if ((unit as any).barrelMesh) {
          (unit as any).barrelMesh.dispose();
          delete (unit as any).barrelMesh;
        }
        that.renderSparks(playerId, { x, y, k }, { x, y, k });
        // Remove the unit from the server units
        that.serverUnits[playerId] = that.serverUnits[playerId].filter(u => u.id !== id);
        // Remove the unit from the debug lines
        let line = that.debugLines[id];
        if (line) {
          line.dispose();
        }
        delete that.debugLines[id];
        // Remove range overlays
        const key = `u${playerId}_${id}`;
        const visionKey = `vision_${key}`;
        const attackKey = `attack_${key}`;
        const stanceKey = `stance_${key}`;
        const guardLineKey = `guard_${key}`;
        if (that.visionRangeOverlays[visionKey]) {
          that.visionRangeOverlays[visionKey].dispose();
          delete that.visionRangeOverlays[visionKey];
        }
        if (that.attackRangeOverlays[attackKey]) {
          that.attackRangeOverlays[attackKey].dispose();
          delete that.attackRangeOverlays[attackKey];
        }
        if (that.stanceIndicators[stanceKey]) {
          that.stanceIndicators[stanceKey].dispose();
          delete that.stanceIndicators[stanceKey];
        }
        if (that.guardPositionLines[guardLineKey]) {
          that.guardPositionLines[guardLineKey].dispose();
          delete that.guardPositionLines[guardLineKey];
        }
        // Remove healthbar
        if (that.healthbars[key]) {
          that.healthbars[key].mesh.dispose();
          that.healthbars[key].texture.dispose();
          delete that.healthbars[key];
        }
      }
    };

    if (k === UnitType.Tank) {
      const turretInstance = isMyUnit
        ? this.baseMeshes.tankTurret.createInstance("tankTurret_" + id)
        : this.baseMeshes.enemyTankTurret.createInstance("enemyTankTurret_" + id);
      unitInstance.addChild(turretInstance);
      turretInstance.position = new Vector3(0, 0.4, 0);
      (unitInstance as any).turretMesh = turretInstance;
      const barrelInstance = isMyUnit
        ? this.baseMeshes.tankBarrel.createInstance("tankBarrel_" + id)
        : this.baseMeshes.enemyTankBarrel.createInstance("enemyTankBarrel_" + id);
      unitInstance.addChild(barrelInstance);
      barrelInstance.position = new Vector3(0, 0.4, 0.4);
      barrelInstance.rotation = new Vector3(-Math.PI / 2, 0, 0);
      (unitInstance as any).barrelMesh = barrelInstance;
    }

    if (k === UnitType.Artillery) {
      // barrel
      const barrelInstance = isMyUnit
        ? this.baseMeshes.artilleryBarrel.createInstance("artilleryBarrel_" + id)
        : this.baseMeshes.enemyArtilleryBarrel.createInstance("enemyArtilleryBarrel_" + id);
      unitInstance.addChild(barrelInstance);
      barrelInstance.position = new Vector3(0, 0.3, 0.2);
      barrelInstance.rotation = new Vector3(-Math.PI / 2 - Math.PI / 6, 0, 0);
      (unitInstance as any).barrelMesh = barrelInstance;
    }

    if (k === UnitType.Tank || k === UnitType.Artillery || k === UnitType.Crawler || k === UnitType.ScoutPlane) {
      // Add targetBeacon child mesh so we can see server actual tx/ty
      const targetBeacon = this.baseMeshes.targetBeacon.createInstance("targetBeacon_" + id);
      targetBeacon.position = this.worldToSectorA1Position(x, y, 0.2);
      (unitInstance as any).targetBeacon = targetBeacon;

      // Change unit's tx and ty setters to update targetBeacon position too
      // As property setters...
      const that = this;
      delete unit.tx;
      delete unit.ty;
      Object.defineProperty(unit, 'tx', {
        get: () => (unit as any)._tx,
        set: (tx: number) => {
          (unit as any)._tx = tx;
          if (targetBeacon && (unit as any)._tx !== undefined && (unit as any)._ty !== undefined) {
            targetBeacon.position = that.worldToSectorA1Position((unit as any)._tx, (unit as any)._ty, 0.2);
          }
        }
      });
      Object.defineProperty(unit, 'ty', {
        get: () => (unit as any)._ty,
        set: (ty: number) => {
          (unit as any)._ty = ty;
          if (targetBeacon && (unit as any)._tx !== undefined && (unit as any)._ty !== undefined) {
            targetBeacon.position = that.worldToSectorA1Position((unit as any)._tx, (unit as any)._ty, 0.2);
          }
        }
      });
      unit.tx = x;
      unit.ty = y;

      // Add a position beacon, too
      const positionBeacon = this.baseMeshes.positionBeacon.createInstance("positionBeacon_" + id);
      positionBeacon.position = this.worldToSectorA1Position(x, y, 0.2);
      (unit as any).positionBeacon = positionBeacon;
    }

    this.serverUnits[playerId].push(unit);

    return unit;
  }

  private handleUnitMove(payload: any) {
    const { playerId, id, x, y, life } = payload;
    const unit = this.serverUnits[playerId]?.find(u => u.id === id);
    if (unit) {
      unit.tx = x;
      unit.ty = y;
      if (life !== undefined) {
        unit.life = life;
        // Update panel if this unit is selected
        if (this.selectedUnit && this.selectedUnit.id === id && this.selectedUnit.playerId === playerId) {
          if (this.infoPanel) {
            this.infoPanel.showUnitInstance(unit);
          }
        }
      }
    }
  }

  private handleBuildingSpawn(payload: any) {
    const { playerId, id, k, x, y } = payload;
    if (!this.serverBuildings[playerId]) {
      this.serverBuildings[playerId] = [];
    }

    // Create building instance from base mesh
    const buildingInstance = this.baseMeshes.building.createInstance("building_" + id);
    buildingInstance.position = this.worldToSectorA1Position(x, y, 0.5);
    this.alignBuildingToPlanet(buildingInstance);

    // Set color based on player
    const material = new StandardMaterial("buildingMat_" + id, this.scene);
    material.diffuseColor = playerId === this.worldPlayerId ?
      new Color3(0.2, 0.6, 0.8) : // Blue for own buildings
      new Color3(0.8, 0.4, 0.2);  // Orange for enemy buildings
    buildingInstance.material = material;

    const that = this;
    const building: Building3D = {
      mesh: buildingInstance,
      id,
      playerId,
      k,
      x,
      y,
      life: payload.life !== undefined ? payload.life : 100, // Initialize life from payload or default
      destroy: () => {
        buildingInstance.dispose();
        // Remove healthbar
        const key = `b${playerId}_${id}`;
        if (that.healthbars[key]) {
          that.healthbars[key].mesh.dispose();
          that.healthbars[key].texture.dispose();
          delete that.healthbars[key];
        }
        // Remove the building from the server buildings
        that.serverBuildings[playerId] = that.serverBuildings[playerId].filter(b => b.id !== id);
      }
    };

    this.serverBuildings[playerId].push(building);

    return building;
  }

  private updateUnitPositions() {
    // Smooth unit movement
    // server: 200 units/second.
    // client: 200 units/second. But is 30fps / 60fps so we need to scale it.
    // 200 --> 100. 500 --> 250.
    const STANDARD_VELOCITY = 50;
    const CRAWLER_VELOCITY = 125;
    const AIR_VELOCITY = 200; // Air units move faster
    const MAX_TURNING_DELTA = Math.PI / 60; // Maximum radians per frame
    const MAX_AIR_TURNING_DELTA = Math.PI / 120; // Maximum radians per frame for air units
    Object.values(this.serverUnits).forEach(units => {
      units.forEach(unit => {

        if (unit.tx === undefined || unit.ty === undefined) {
          unit.tx = unit.x;
          unit.ty = unit.y;
          unit.vx = 0;
          unit.vy = 0;
        } else {
          const xDelta = unit.tx - unit.x;
          const yDelta = unit.ty - unit.y;
          const targetAngle = Math.atan2(yDelta, xDelta);
          const distance = Math.sqrt(xDelta * xDelta + yDelta * yDelta);

          const isAirUnit = unit.k === UnitType.ScoutPlane;
          const maxVelocity = isAirUnit ? AIR_VELOCITY :
            (unit.k === UnitType.Crawler ? CRAWLER_VELOCITY : STANDARD_VELOCITY);
          const desiredSpeed = maxVelocity / this.GAME_FPS;
          const maxTurnRate = isAirUnit ? MAX_AIR_TURNING_DELTA : MAX_TURNING_DELTA;

          // Get current velocity direction
          const currentVelocity = Math.sqrt(unit.vx * unit.vx + unit.vy * unit.vy);
          let currentAngle = currentVelocity > 0.01 ? Math.atan2(unit.vy, unit.vx) : targetAngle;

          // Turn towards target (with max turn rate)
          let angleDiff = targetAngle - currentAngle;
          while (angleDiff > Math.PI) angleDiff -= 2 * Math.PI;
          while (angleDiff < -Math.PI) angleDiff += 2 * Math.PI;

          if (Math.abs(angleDiff) > maxTurnRate) {
            currentAngle += Math.sign(angleDiff) * maxTurnRate;
          } else {
            currentAngle = targetAngle;
          }

          // Future ... Air units: maintain speed, always moving
          // Ground units: decelerate when close to target, stop when reached
          let minSpeed = isAirUnit ? maxVelocity * 0.5 : 0;
          let speed: number;
          const stopDistance = desiredSpeed * 10; // Start decelerating when this close
          if (distance < stopDistance) {
            speed = Math.max(minSpeed, currentVelocity * 0.9);
            // Stop completely when very close
            if (distance < 1) {
              speed = minSpeed;
            }
          } else {
            // Accelerate towards desired speed
            speed = Math.min(currentVelocity + desiredSpeed * 0.1, desiredSpeed);
          }

          unit.vx = Math.cos(currentAngle) * speed;
          unit.vy = Math.sin(currentAngle) * speed;

          // Apply server-client desync penalty (only for crawlers)
          let ratio = 1;
          if (unit.k === 5 && unit.ax !== undefined && unit.ay !== undefined) {
            unit.ax += unit.vx;
            unit.ay += unit.vy;

            if ((unit as any).positionBeacon) {
              (unit as any).positionBeacon.position = this.worldToSectorA1Position(unit.ax, unit.ay, 0.2);
            }

            const axDelta = unit.x - unit.ax;
            const ayDelta = unit.y - unit.ay;
            const serverClientUnitDelta = Math.sqrt(axDelta * axDelta + ayDelta * ayDelta);
            if (serverClientUnitDelta > 50) {
              const axtxDelta = unit.tx - unit.ax;
              const axtyDelta = unit.ty - unit.ay;
              const serverDistance = Math.sqrt(axtxDelta * axtxDelta + axtyDelta * axtyDelta);
              if (distance > serverDistance) {
                ratio = 1.2;
              } else {
                ratio = 0.8;
              }
            }
          }

          // Apply velocity
          unit.x += unit.vx * ratio;
          unit.y += unit.vy * ratio;

          if ((unit as any).positionBeacon) {
            (unit as any).positionBeacon.position = this.worldToSectorA1Position(unit.x, unit.y, 0.2);
          }

          // Air units stay at higher Y position
          const heightOffset = unit.k === UnitType.ScoutPlane ? 8 : 0.5;
          unit.mesh.position = this.worldToSectorA1Position(unit.x, unit.y, heightOffset);

          // Update unit rotation to face movement direction in sector A1's coordinate space
          if ((unit.k === UnitType.Tank || unit.k === UnitType.Artillery || unit.k === UnitType.Worker || unit.k === UnitType.Crawler || unit.k === UnitType.ScoutPlane) && (unit.vx !== 0 || unit.vy !== 0)) {
            // Calculate rotation angle in sector A1's local coordinate space
            const targetAngle = this.calculateSectorA1Rotation(unit.vx, unit.vy);
            unit.currentFacing = targetAngle;
            // Apply rotation around the sector's normal axis (Y axis after base rotation)
            // The base rotation sets (pitch, baseYaw, roll), we need to add targetAngle to baseYaw
            // Store base yaw if not already stored
            if ((unit.mesh as any).baseYaw === undefined) {
              (unit.mesh as any).baseYaw = unit.mesh.rotation.y;
            }
            // Add target angle to base yaw for final rotation
            unit.mesh.rotation.y = (unit.mesh as any).baseYaw + targetAngle;
            // Note: turret and barrel are children of unit.mesh, so they automatically inherit the rotation
          }

          // Draw a line from the unit to the target
          let line = this.debugLines[unit.id];
          if (line) {
            // update the points
            const points = [this.worldToSectorA1Position(unit.x, unit.y, 0.2), this.worldToSectorA1Position(unit.tx, unit.ty, 0.2)];
            line = MeshBuilder.CreateLines(null, { points, instance: line as any, updatable: true });
          } else {
            line = MeshBuilder.CreateLines("line_" + unit.id, {
              points: [this.worldToSectorA1Position(unit.x, unit.y, 0.2), this.worldToSectorA1Position(unit.tx, unit.ty, 0.2)],
              updatable: true
            }, this.scene);
            (line as any).color = new Color3(1, 1, 1);
            this.debugLines[unit.id] = line;
          }

        }
      });
    });
  }

  private updateCameraPosition(): void {
    const previousPosition = this.camera.position.clone();

    if (this.cameraMode === 'orbit') {
      this.updateOrbitCamera();
    } else {
      // Free camera mode (original 6DOF movement)
      let speed = 1.0;
      if (this.isInSpaceLayer) {
        speed = 10;
      }

      if (this.keyState.w) {
        this.camera.position.addInPlace(this.camera.getDirection(Vector3.Forward()).scale(speed));
      }
      if (this.keyState.s) {
        this.camera.position.addInPlace(this.camera.getDirection(Vector3.Backward()).scale(speed));
      }
      if (this.keyState.a) {
        this.camera.position.addInPlace(this.camera.getDirection(Vector3.Left()).scale(speed));
      }
      if (this.keyState.d) {
        this.camera.position.addInPlace(this.camera.getDirection(Vector3.Right()).scale(speed));
      }
      if (this.keyState.x) {
        this.camera.position.addInPlace(new Vector3(0, -1, 0).scale(speed));
      }
      if (this.keyState.c) {
        this.camera.position.addInPlace(new Vector3(0, 1, 0).scale(speed));
      }
    }

    // Check if camera has moved significantly and schedule save
    this.checkAndScheduleCameraSave(previousPosition);
  }

  private updateOrbitCamera(): void {
    const halfWidth = (this.WORLD_SIZE / this.COORDINATE_SCALE) / 2;
    const PLANET_CENTER_X = halfWidth;
    const PLANET_CENTER_Y = -500;
    const PLANET_CENTER_Z = halfWidth;
    const planetCenter = new Vector3(PLANET_CENTER_X, PLANET_CENTER_Y, PLANET_CENTER_Z);

    // Speed constants
    const orbitAcceleration = 0.0005; // Acceleration per frame for keyboard
    const heightAcceleration = 0.3; // Acceleration for radius changes
    const mouseSensitivity = 0.003; // Mouse pan sensitivity
    const friction = 0.92; // Friction factor (0.92 means 8% velocity loss per frame)
    const minRadius = 100;
    const maxRadius = 2000;
    const minElevation = 0.1; // Prevent going below planet
    const maxElevation = Math.PI - 0.1; // Prevent going above planet

    // Apply keyboard input to velocities
    if (this.keyState.a) {
      this.orbitAngleVelocity -= orbitAcceleration;
    }
    if (this.keyState.d) {
      this.orbitAngleVelocity += orbitAcceleration;
    }
    if (this.keyState.w) {
      this.orbitElevationVelocity -= orbitAcceleration;
    }
    if (this.keyState.s) {
      this.orbitElevationVelocity += orbitAcceleration;
    }
    if (this.keyState.x) {
      this.orbitRadiusVelocity -= heightAcceleration;
    }
    if (this.keyState.c) {
      this.orbitRadiusVelocity += heightAcceleration;
    }

    // Apply friction to velocities (inertia)
    this.orbitAngleVelocity *= friction;
    this.orbitElevationVelocity *= friction;
    this.orbitRadiusVelocity *= friction;
    this.cameraOffsetPitchVelocity *= friction;
    this.cameraOffsetYawVelocity *= friction;

    // Stop very small velocities to prevent jitter
    if (Math.abs(this.orbitAngleVelocity) < 0.0001) {
      this.orbitAngleVelocity = 0;
    }
    if (Math.abs(this.orbitElevationVelocity) < 0.0001) {
      this.orbitElevationVelocity = 0;
    }
    if (Math.abs(this.orbitRadiusVelocity) < 0.01) {
      this.orbitRadiusVelocity = 0;
    }
    if (Math.abs(this.cameraOffsetPitchVelocity) < 0.0001) {
      this.cameraOffsetPitchVelocity = 0;
    }
    if (Math.abs(this.cameraOffsetYawVelocity) < 0.0001) {
      this.cameraOffsetYawVelocity = 0;
    }

    // Update orbit state from velocities
    this.orbitAngle += this.orbitAngleVelocity;
    this.orbitElevation += this.orbitElevationVelocity;
    this.orbitRadius += this.orbitRadiusVelocity;
    this.cameraOffsetPitch += this.cameraOffsetPitchVelocity;
    this.cameraOffsetYaw += this.cameraOffsetYawVelocity;

    // Clamp values
    this.orbitElevation = Math.max(minElevation, Math.min(maxElevation, this.orbitElevation));
    this.orbitRadius = Math.max(minRadius, Math.min(maxRadius, this.orbitRadius));
    // Clamp offset angles to reasonable limits (about ±60 degrees)
    const maxOffsetAngle = Math.PI / 3; // 60 degrees
    this.cameraOffsetPitch = Math.max(-maxOffsetAngle, Math.min(maxOffsetAngle, this.cameraOffsetPitch));
    // Yaw can rotate freely (no clamp needed, or clamp to ±180 if desired)

    // Calculate camera position using spherical coordinates
    // x = centerX + radius * sin(elevation) * cos(angle)
    // y = centerY + radius * cos(elevation)
    // z = centerZ + radius * sin(elevation) * sin(angle)
    const x = planetCenter.x + this.orbitRadius * Math.sin(this.orbitElevation) * Math.cos(this.orbitAngle);
    const y = planetCenter.y + this.orbitRadius * Math.cos(this.orbitElevation);
    const z = planetCenter.z + this.orbitRadius * Math.sin(this.orbitElevation) * Math.sin(this.orbitAngle);

    this.camera.position = new Vector3(x, y, z);

    const planetNormal = planetCenter.subtract(this.camera.position).normalize();
    const offsetVector = planetNormal.scale(1).add(new Vector3(0, this.cameraOffsetPitch, 0)).normalize();
    const targetPoint = this.camera.position.add(offsetVector);
    this.camera.setTarget(targetPoint);
  }

  private checkAndScheduleCameraSave(previousPosition: Vector3): void {
    const currentPosition = this.camera.position;
    const distance = Vector3.Distance(previousPosition, currentPosition);

    // Check if camera has moved more than epsilon OR if rotation has changed significantly
    const rotationChanged = this.lastSavedCameraPosition === null ||
      (this.camera.rotation && this.lastSavedCameraPosition &&
        (Math.abs(this.camera.rotation.x) > 0.01 || Math.abs(this.camera.rotation.y) > 0.01 || Math.abs(this.camera.rotation.z) > 0.01));

    if (this.cameraSaveTimeout === null && (distance > this.CAMERA_POSITION_EPSILON || rotationChanged)) {
      this.scheduleCameraSave();
    }
  }

  private scheduleCameraSave(): void {
    // Clear existing timeout
    if (this.cameraSaveTimeout !== null) {
      clearTimeout(this.cameraSaveTimeout);
    }

    // Set new timeout for debounced save
    this.cameraSaveTimeout = window.setTimeout(() => {
      // Not in space layer
      if (!this.isInSpaceLayer) {
        this.saveCameraPosition();
      }
      this.cameraSaveTimeout = null;
    }, this.CAMERA_SAVE_THROTTLE_MS);
  }

  private saveCameraPosition(): void {
    // Only save camera position in free mode
    // Orbit mode uses its own state variables
    if (this.cameraMode !== 'free') {
      return;
    }

    const position = this.camera.position;
    const rotation = this.camera.rotation;
    const cameraData = {
      x: position.x,
      y: position.y,
      z: position.z,
      rotationX: rotation.x,
      rotationY: rotation.y,
      rotationZ: rotation.z,
      timestamp: Date.now()
    };

    try {
      localStorage.setItem(this.CAMERA_POSITION_KEY, JSON.stringify(cameraData));
      this.lastSavedCameraPosition = position.clone();
      console.log('Camera position and rotation saved to localStorage');
    } catch (error) {
      console.warn('Failed to save camera position to localStorage:', error);
    }
  }

  private loadCameraPosition(): void {
    // Only load saved camera position in free mode
    // Orbit mode uses its own state variables
    if (this.cameraMode !== 'free') {
      return;
    }

    try {
      const savedData = localStorage.getItem(this.CAMERA_POSITION_KEY);
      if (savedData) {
        const cameraData = JSON.parse(savedData);
        const position = new Vector3(cameraData.x, cameraData.y, cameraData.z);

        // Only restore if the saved position is valid
        if (position && !isNaN(position.x) && !isNaN(position.y) && !isNaN(position.z)) {
          this.camera.position = position;
          this.lastSavedCameraPosition = position.clone();

          // Restore rotation if available
          if (cameraData.rotationX !== undefined && cameraData.rotationY !== undefined && cameraData.rotationZ !== undefined) {
            const rotation = new Vector3(cameraData.rotationX, cameraData.rotationY, cameraData.rotationZ);
            if (!isNaN(rotation.x) && !isNaN(rotation.y) && !isNaN(rotation.z)) {
              this.camera.rotation = rotation;
            }
          }

          console.log('Camera position and rotation restored from localStorage');
        }
      }
    } catch (error) {
      console.warn('Failed to load camera position from localStorage:', error);
    }
  }

  /**
   * Toggle between free and orbit camera modes
   */
  private toggleCameraMode(): void {
    if (this.cameraMode === 'free') {
      // Save free camera state before switching
      this.savedFreeCameraPosition = this.camera.position.clone();
      this.savedFreeCameraRotation = this.camera.rotation ? this.camera.rotation.clone() : null;

      this.cameraMode = 'orbit';
      // Detach mouse controls for orbit mode (we handle mouse manually)
      this.camera.detachControl();
      // Reset mouse state
      this.isMouseDown = false;
      this.isRightMouseDown = false;
      this.hasMouseMoved = false;
      // Reset offset state (optional - could preserve it if desired)
      // this.cameraOffsetPitch = 0;
      // this.cameraOffsetYaw = 0;
      // this.cameraOffsetPitchVelocity = 0;
      // this.cameraOffsetYawVelocity = 0;
      // Initialize orbit camera position
      this.initializeOrbitCamera();
      console.log('Switched to orbit camera mode');
    } else {
      this.cameraMode = 'free';
      // Reset mouse state
      this.isMouseDown = false;
      this.isRightMouseDown = false;
      this.hasMouseMoved = false;
      // Reset orbit velocities
      this.orbitAngleVelocity = 0;
      this.orbitElevationVelocity = 0;
      this.orbitRadiusVelocity = 0;
      // Reset offset velocities
      this.cameraOffsetPitchVelocity = 0;
      this.cameraOffsetYawVelocity = 0;
      // Restore free camera state if available
      if (this.savedFreeCameraPosition) {
        this.camera.position = this.savedFreeCameraPosition.clone();
        if (this.savedFreeCameraRotation) {
          this.camera.rotation = this.savedFreeCameraRotation.clone();
        }
      }
      // Reattach mouse controls for free mode
      this.camera.attachControl(this.canvas, true);
      console.log('Switched to free camera mode');
    }
  }

  /**
   * Initialize orbit camera position based on current orbit state
   */
  private initializeOrbitCamera(): void {
    const halfWidth = (this.WORLD_SIZE / this.COORDINATE_SCALE) / 2;
    const PLANET_CENTER_X = halfWidth;
    const PLANET_CENTER_Y = -512;
    const PLANET_CENTER_Z = halfWidth;
    const planetCenter = new Vector3(PLANET_CENTER_X, PLANET_CENTER_Y, PLANET_CENTER_Z);

    // Calculate initial position
    const x = planetCenter.x + this.orbitRadius * Math.sin(this.orbitElevation) * Math.cos(this.orbitAngle);
    const y = planetCenter.y + this.orbitRadius * Math.cos(this.orbitElevation);
    const z = planetCenter.z + this.orbitRadius * Math.sin(this.orbitElevation) * Math.sin(this.orbitAngle);

    this.camera.position = new Vector3(x, y, z);
    this.camera.setTarget(planetCenter);
  }

  /**
   * Toggle between ground and space layer
   */
  private toggleSpaceLayer(): void {
    const SPACE_LAYER_HEIGHT = 1000;
    const halfWidth = (this.WORLD_SIZE / this.COORDINATE_SCALE) / 2;

    if (!this.isInSpaceLayer) {
      // Save current ground position and rotation
      this.groundCameraPosition = this.camera.position.clone();
      this.groundCameraRotation = this.camera.rotation.clone();

      // Move camera to space layer
      // Position camera above the center, looking down at the space layer
      this.camera.position = new Vector3(halfWidth, SPACE_LAYER_HEIGHT + 500, halfWidth);
      this.camera.setTarget(new Vector3(halfWidth, SPACE_LAYER_HEIGHT, halfWidth));

      this.isInSpaceLayer = true;
      console.log('Switched to space layer');
    } else {
      // Restore ground position and rotation
      if (this.groundCameraPosition && this.groundCameraRotation) {
        this.camera.position = this.groundCameraPosition;
        this.camera.rotation = this.groundCameraRotation;
      } else {
        // Fallback to default ground position
        this.camera.position = new Vector3(0, 100, 0);
        this.camera.setTarget(Vector3.Zero());
      }

      this.isInSpaceLayer = false;
      console.log('Switched to ground layer');
    }
  }

  // Cleanup method to clear timeout when game is destroyed
  public destroy(): void {
    if (this.cameraSaveTimeout !== null) {
      clearTimeout(this.cameraSaveTimeout);
      this.cameraSaveTimeout = null;
    }

    if (this.fogOfWar) {
      this.fogOfWar.dispose();
    }

    // Clean up range overlays
    Object.values(this.visionRangeOverlays).forEach(overlay => overlay.dispose());
    Object.values(this.attackRangeOverlays).forEach(overlay => overlay.dispose());
    Object.values(this.stanceIndicators).forEach(overlay => overlay.dispose());
    Object.values(this.guardPositionLines).forEach(line => line.dispose());
    Object.values(this.healthbars).forEach(healthbar => {
      healthbar.mesh.dispose();
      healthbar.texture.dispose();
    });
    Object.values(this.sectorLabels).forEach(label => {
      label.mesh.dispose();
      label.texture.dispose();
    });
    // Clean up rockets
    this.rockets.forEach(rocket => rocket.mesh.dispose());
    this.rockets = [];
    // Clean up laser beams
    this.laserBeams.forEach(beam => beam.mesh.dispose());
    this.laserBeams = [];
    // Clean up missiles
    this.missiles.forEach(missile => missile.mesh.dispose());
    this.missiles = [];
    // Clean up space ships
    this.spaceShips.forEach(ship => ship.mesh.dispose());
    this.spaceShips = [];
    this.visionRangeOverlays = {};
    this.attackRangeOverlays = {};
    this.stanceIndicators = {};
    this.guardPositionLines = {};
    this.healthbars = {};
    this.sectorLabels = {};
  }

  private updateSelectedInstancePanel(): void {
    // Update panel if a unit or building is selected (to refresh position/health)
    if (this.selectedUnit && this.infoPanel) {
      this.infoPanel.showUnitInstance(this.selectedUnit);
    } else if (this.selectedBuilding && this.infoPanel) {
      this.infoPanel.showBuildingInstance(this.selectedBuilding);
    }
  }

  private updateSparks(): void {
    const sparksToRemove = [];
    for (let i = 0; i < this.sparks.length; i++) {
      const spark = this.sparks[i];
      // Move the spark towards the target in 3D space
      const targetPos = (spark as any).targetPosition as Vector3;
      const direction = targetPos.subtract(spark.position);
      const distance = direction.length();

      if (distance > 0.1) {
        const normalized = direction.normalize();
        const moveDistance = (spark as any).velocity / this.GAME_FPS;
        spark.position.addInPlace(normalized.scale(moveDistance));
      } else {
        spark.position = targetPos.clone();
      }

      (spark as any).lifetimeTicks--;
      if (!(spark as any).isAttack) {
        (spark as any).velocity *= 0.95;
      }
      if ((spark as any).lifetimeTicks <= 0) {
        sparksToRemove.push(i);
      }
    };
    sparksToRemove.reverse();
    sparksToRemove.forEach(i => {
      this.sparks[i].dispose();
      this.sparks.splice(i, 1);
    });
  }

  /**
   * Check if orbital mass drivers should launch rockets and launch them
   */
  private checkAndLaunchRockets(): void {
    const currentTime = Date.now();
    const LAUNCH_INTERVAL_MS = 10000; // 10 seconds

    Object.values(this.serverBuildings).forEach(buildings => {
      buildings.forEach(building => {
        if (building.k === BuildingType.OrbitalMassDriver) {
          const key = `${building.playerId}_${building.id}`;
          const lastLaunch = this.orbitalMassDriverLastLaunch[key] || 0;

          if (currentTime - lastLaunch >= LAUNCH_INTERVAL_MS) {
            this.launchRocket(building);
            this.orbitalMassDriverLastLaunch[key] = currentTime;
          }
        }
      });
    });
  }

  /**
   * Launch a rocket from an orbital mass driver
   */
  private launchRocket(building: Building3D): void {
    const rocketInstance = this.baseMeshes.rocket.createInstance(`rocket_${Date.now()}_${building.id}`);

    // Position rocket at the top of the orbital mass driver
    rocketInstance.position = this.worldToSectorA1Position(building.x, building.y, 1.5);
    rocketInstance.material = this.materials.rocket;

    // Add rocket to tracking array
    this.rockets.push({
      mesh: rocketInstance,
      velocity: 0, // Units per frame
      lifetime: 300 // Frames (2 seconds at 60fps)
    });
  }

  /**
   * Render a laser beam from source to target
   */
  private renderLaserBeam(sourceX: number, sourceY: number, targetX: number, targetY: number): void {
    // Transform source and target to sector A1
    const sourcePos = this.worldToSectorA1Position(sourceX, sourceY, 1.5);
    const targetPos = this.worldToSectorA1Position(targetX, targetY, 1.5);
    const direction = targetPos.subtract(sourcePos);
    const distance = direction.length();
    const midPos = sourcePos.add(direction.scale(0.5));

    // Create a cylinder for the laser beam
    const laserBeam = MeshBuilder.CreateCylinder(
      `laserBeam_${Date.now()}`,
      { height: distance, diameterTop: 0.15, diameterBottom: 0.15, tessellation: 8 },
      this.scene
    );

    // Position the beam at the midpoint
    laserBeam.position = midPos;

    // Rotate the cylinder to point from source to target
    // Cylinder default orientation is along Y axis (vertical), we need it to point along direction
    laserBeam.lookAt(targetPos);
    // Adjust for cylinder's default orientation (pointing up)
    laserBeam.rotate(Vector3.Right(), Math.PI / 2);

    // Apply material
    const material = new StandardMaterial(`laserBeamMat_${Date.now()}`, this.scene);
    material.diffuseColor = new Color3(0.1, 0.3, 0.9);
    material.emissiveColor = new Color3(0.2, 0.4, 1.0);
    material.alpha = 0.8;
    material.disableLighting = true;
    laserBeam.material = material;

    // Add to tracking array
    this.laserBeams.push({
      mesh: laserBeam,
      lifetime: 10 // Very short lifetime for instant beam effect (10 frames = ~0.17 seconds)
    });
  }

  /**
   * Render a missile projectile from source to target
   */
  private renderMissile(sourceX: number, sourceY: number, targetX: number, targetY: number): void {
    const missileInstance = this.baseMeshes.missile.createInstance(`missile_${Date.now()}`);
    const sourcePos = this.worldToSectorA1Position(sourceX, sourceY, 1.5);
    const targetPos = this.worldToSectorA1Position(targetX, targetY, 1.5);
    missileInstance.position = sourcePos;
    missileInstance.material = this.materials.missile;

    // Calculate direction and velocity in 3D space
    const direction = targetPos.subtract(sourcePos);
    const distance = direction.length();
    const speed = 15; // Units per frame
    const velocity = direction.normalize().scale(speed);

    // Calculate target Z position (same as source for ground targets)
    // Rotate missile to face direction
    missileInstance.lookAt(targetPos);

    // Add to tracking array
    this.missiles.push({
      mesh: missileInstance,
      velocity: velocity,
      lifetime: Math.ceil(distance / speed) + 10, // Travel time plus buffer
      targetX: targetPos.x,
      targetY: targetPos.y,
      targetZ: targetPos.z
    });
  }

  /**
   * Update laser beam animations and remove expired beams
   */
  private updateLaserBeams(): void {
    const beamsToRemove: number[] = [];

    for (let i = 0; i < this.laserBeams.length; i++) {
      const beam = this.laserBeams[i];

      // Fade out the beam
      const material = beam.mesh.material as StandardMaterial;
      beam.lifetime--;
      material.alpha = Math.max(0, (beam.lifetime / 10) * 0.8);

      // Remove if lifetime expired
      if (beam.lifetime <= 0) {
        beamsToRemove.push(i);
      }
    }

    // Remove expired beams (reverse to maintain indices)
    beamsToRemove.reverse().forEach(i => {
      this.laserBeams[i].mesh.dispose();
      this.laserBeams.splice(i, 1);
    });
  }

  /**
   * Update missile positions and remove expired missiles
   */
  private updateMissiles(): void {
    const missilesToRemove: number[] = [];

    for (let i = 0; i < this.missiles.length; i++) {
      const missile = this.missiles[i];

      // Move missile towards target
      missile.mesh.position.addInPlace(missile.velocity);

      // Check if missile has reached target (with some tolerance)
      const currentPos = missile.mesh.position;
      const targetPos = new Vector3(missile.targetX, missile.targetY, missile.targetZ);
      const distanceToTarget = Vector3.Distance(currentPos, targetPos);

      // Decrease lifetime
      missile.lifetime--;

      // Remove if lifetime expired or reached target
      if (missile.lifetime <= 0 || distanceToTarget < 0.5) {
        // Create explosion effect at target
        // Note: targetX/Y/Z are now 3D coordinates, need to convert back to world coords for sparks
        // For now, use approximate conversion (this is a simplification)
        if (distanceToTarget < 0.5) {
          // Approximate world coordinates from 3D position (reverse transform would be complex)
          // Using a simple approximation: assume target is near sector center
          const approxWorldX = (missile.targetX - (this.sectorA1Center?.x || 0)) * this.COORDINATE_SCALE + this.WORLD_SIZE / 2;
          const approxWorldY = (missile.targetZ - (this.sectorA1Center?.z || 0)) * this.COORDINATE_SCALE + this.WORLD_SIZE / 2;
          this.renderSparks(
            -1, // No specific player
            { x: approxWorldX, y: approxWorldY, k: 0 },
            { x: approxWorldX, y: approxWorldY, k: 0 },
            true
          );
        }
        missilesToRemove.push(i);
      }
    }

    // Remove expired missiles (reverse to maintain indices)
    missilesToRemove.reverse().forEach(i => {
      this.missiles[i].mesh.dispose();
      this.missiles.splice(i, 1);
    });
  }

  /**
   * Update rocket positions and remove expired rockets
   */
  private updateRockets(): void {
    const rocketsToRemove: number[] = [];

    for (let i = 0; i < this.rockets.length; i++) {
      const rocket = this.rockets[i];

      rocket.mesh.position.y += rocket.velocity;
      // Use a polynomial approach for softer acceleration towards max velocity
      const maxVelocity = 5;
      // For a really long delay, use a low initial acceleration that grows
      // Example: cubic ease out; also clamp initial acceleration at a much lower value
      // This keeps the rocket slow for much longer before ramping up
      let initialPhase = Math.min(rocket.lifetime, 300); // Last for ~100 frames at slow acceleration
      let baseAccel = initialPhase < 300
        ? 0.001 * (1 - rocket.velocity / maxVelocity) ** 3  // Very slow at start (cubic and small factor)
        : 0.003 * (1 - rocket.velocity / maxVelocity) ** 2;   // Normal after "delay" period
      rocket.velocity += baseAccel;
      if (rocket.velocity > maxVelocity) {
        rocket.velocity = maxVelocity;
      }

      // Decrease lifetime
      rocket.lifetime--;

      // Remove if lifetime expired or too high
      if (rocket.lifetime <= 0 || rocket.mesh.position.y > 500) {
        rocketsToRemove.push(i);
      }
    }

    // Remove expired rockets (reverse to maintain indices)
    rocketsToRemove.reverse().forEach(i => {
      this.rockets[i].mesh.dispose();
      this.rockets.splice(i, 1);
    });
  }

  /**
   * Update space ship orbital positions and rotations
   */
  private updateSpaceShips(): void {
    const SPACE_LAYER_HEIGHT = 1000;
    const PLANET_CENTER_Y = -500; // Planet is at y = -500

    this.spaceShips.forEach(ship => {
      // Update orbital angle
      ship.angle += ship.orbitalSpeed;
      if (ship.angle > Math.PI * 2) {
        ship.angle -= Math.PI * 2;
      }

      // Calculate new orbital position
      const x = ship.centerX + Math.cos(ship.angle) * ship.radius;
      const y = ship.centerY;
      const z = ship.centerZ + Math.sin(ship.angle) * ship.radius;
      ship.mesh.position.x = x;
      ship.mesh.position.y = y;
      ship.mesh.position.z = z;

      // Point ship down towards planet center
      // Calculate direction vector from ship to planet center
      const dx = ship.centerX - x;
      const dy = ship.centerY - y;
      const dz = ship.centerZ - z;

      // Calculate horizontal rotation (yaw) to face planet center
      const horizontalAngle = Math.atan2(dx, dz);
      ship.mesh.rotation.y = horizontalAngle;

      // Calculate vertical rotation (pitch) to point down
      const horizontalDistance = Math.sqrt(dx * dx + dz * dz);
      const pitchAngle = Math.atan2(dy, horizontalDistance) + Math.PI / 2;
      ship.mesh.rotation.x = pitchAngle;
    });
  }

  /**
   * Update range overlays for all units
   */
  private updateRangeOverlays(): void {
    // Collect all current unit keys
    const currentUnitKeys = new Set<string>();
    Object.values(this.serverUnits).forEach(units => {
      units.forEach(unit => {
        const key = `u${unit.playerId}_${unit.id}`;
        currentUnitKeys.add(key);

        const scaledX = unit.x / this.COORDINATE_SCALE;
        const scaledY = unit.y / this.COORDINATE_SCALE;

        // Update vision range overlay
        if (this.showVisionRange) {
          const visionKey = `vision_${key}`;
          const visionRange = UNIT_DEFINITIONS[unit.k]?.sightRange || 300;
          this.createOrUpdateRangeOverlay(
            visionKey,
            scaledX,
            scaledY,
            visionRange / this.COORDINATE_SCALE,
            new Color3(0, 1, 0), // Green for vision
            this.visionRangeOverlays
          );
        }

        // Update attack range overlay
        if (this.showAttackRange) {
          const weaponType = UNIT_DEFINITIONS[unit.k]?.defaultWeapon ?? null;
          if (weaponType) {
            const attackKey = `attack_${key}`;
            const attackRange = WeaponStatsMap[weaponType].range;
            this.createOrUpdateRangeOverlay(
              attackKey,
              scaledX,
              scaledY,
              attackRange / this.COORDINATE_SCALE,
              new Color3(1, 0, 0), // Red for attack
              this.attackRangeOverlays
            );
          }
        }

        // Update stance indicator
        if (this.showStanceIndicators) {
          const stanceKey = `stance_${key}`;
          const stance = unit.s || UnitStance.StandGround;
          let stanceColor: Color3;
          if (stance === UnitStance.StandGround) {
            stanceColor = new Color3(0.5, 0.5, 0.5); // Grey
          } else if (stance === UnitStance.Defensive) {
            stanceColor = new Color3(0, 0, 1); // Blue
          } else { // Aggressive
            stanceColor = new Color3(1, 0, 0); // Red
          }
          // Small circle around unit (radius of 1.0 in scaled coordinates)
          this.createOrUpdateRangeOverlay(
            stanceKey,
            scaledX,
            scaledY,
            1.0,
            stanceColor,
            this.stanceIndicators
          );

          // For Defensive stance, draw a line to guard position
          if (stance === UnitStance.Defensive && unit.gx !== undefined && unit.gy !== undefined) {
            const guardLineKey = `guard_${key}`;
            this.createOrUpdateGuardLine(
              guardLineKey,
              unit.x,
              unit.y,
              unit.gx,
              unit.gy,
              stanceColor
            );
          }
        }
      });
    });

    // For buildings too
    Object.values(this.serverBuildings).forEach(buildings => {
      buildings.forEach(building => {
        const key = `b${building.playerId}_${building.id}`;
        currentUnitKeys.add(key);
        const scaledX = building.x / this.COORDINATE_SCALE;
        const scaledY = building.y / this.COORDINATE_SCALE;

        // Update vision range overlay
        if (this.showVisionRange) {
          const visionKey = `vision_${key}`;
          const visionRange = BUILDING_DEFINITIONS[building.k]?.sightRange || 300;
          this.createOrUpdateRangeOverlay(
            visionKey,
            scaledX,
            scaledY,
            visionRange / this.COORDINATE_SCALE,
            new Color3(0, 1, 0), // Green for vision
            this.visionRangeOverlays
          )
        }

        // Update attack range overlay
        if (this.showAttackRange) {
          const weaponType = BUILDING_DEFINITIONS[building.k]?.defaultWeapon ?? null;
          if (weaponType) {
            const attackKey = `attack_${key}`;
            const attackRange = WeaponStatsMap[weaponType].range;

            this.createOrUpdateRangeOverlay(
              attackKey,
              scaledX,
              scaledY,
              attackRange / this.COORDINATE_SCALE,
              new Color3(1, 0, 0), // Red for attack
              this.attackRangeOverlays
            )
          }
        }
      });
    });

    // Remove vision overlays if toggle is off or unit no longer exists
    Object.keys(this.visionRangeOverlays).forEach(key => {
      const unitKey = key.replace('vision_', '');
      if (!this.showVisionRange || !currentUnitKeys.has(unitKey)) {
        this.visionRangeOverlays[key].dispose();
        delete this.visionRangeOverlays[key];
      }
    });

    // Remove attack overlays if toggle is off or unit no longer exists
    Object.keys(this.attackRangeOverlays).forEach(key => {
      const unitKey = key.replace('attack_', '');
      if (!this.showAttackRange || !currentUnitKeys.has(unitKey)) {
        this.attackRangeOverlays[key].dispose();
        delete this.attackRangeOverlays[key];
      }
    });

    // Remove stance indicators if toggle is off or unit no longer exists
    Object.keys(this.stanceIndicators).forEach(key => {
      const unitKey = key.replace('stance_', '');
      if (!this.showStanceIndicators || !currentUnitKeys.has(unitKey)) {
        this.stanceIndicators[key].dispose();
        delete this.stanceIndicators[key];
      }
    });

    // Remove guard position lines if toggle is off or unit no longer exists
    Object.keys(this.guardPositionLines).forEach(key => {
      const unitKey = key.replace('guard_', '');
      if (!this.showStanceIndicators || !currentUnitKeys.has(unitKey)) {
        this.guardPositionLines[key].dispose();
        delete this.guardPositionLines[key];
      }
    });
  }

  /**
   * Create or update a range overlay circle
   */
  private createOrUpdateRangeOverlay(
    key: string,
    x: number,
    y: number,
    radius: number,
    color: Color3,
    overlayMap: { [key: string]: Mesh }
  ): void {
    let overlay = overlayMap[key];
    if (!overlay) {
      // Create a torus (ring) to show the range circle
      overlay = MeshBuilder.CreateTorus(
        `rangeOverlay_${key}`,
        {
          diameter: radius * 2,
          thickness: radius * 0.05, // 5% of radius
          tessellation: 32
        },
        this.scene
      );
      overlay.position.y = 0.1; // Slightly above ground

      // Create material
      const material = new StandardMaterial(`rangeMaterial_${key}`, this.scene);
      material.diffuseColor = color;
      material.emissiveColor = color;
      material.alpha = 0.3;
      material.disableLighting = true;
      material.transparencyMode = 2;
      overlay.material = material;
      overlayMap[key] = overlay;
    }

    // Update colour if needed
    const material = overlay.material as StandardMaterial;
    if (color !== material.diffuseColor) {
      material.diffuseColor = color;
      material.emissiveColor = color;
    }

    // Update position
    overlay.position.x = x;
    overlay.position.z = y;
    overlay.isVisible = true;
  }

  /**
   * Update healthbars for all units and buildings
   */
  private updateHealthbars(): void {
    // Collect all current unit and building keys
    const currentObjectKeys = new Set<string>();

    // Update healthbars for units
    Object.values(this.serverUnits).forEach(units => {
      units.forEach(unit => {
        const key = `u${unit.playerId}_${unit.id}`;
        currentObjectKeys.add(key);

        if (this.showHealthbars) {
          const scaledX = unit.x / this.COORDINATE_SCALE;
          const scaledY = unit.y / this.COORDINATE_SCALE;
          // Position healthbar above unit using mesh's actual Y position
          const healthbarY = unit.mesh.position.y + 0.8; // 0.8 units above the unit

          // Get unit's max health
          const maxHealth = UNIT_DEFINITIONS[unit.k]?.life || 100;
          const currentHealth = unit.life ?? maxHealth;
          const healthPercent = Math.max(0, Math.min(1, currentHealth / maxHealth));

          this.createOrUpdateHealthbar(
            key,
            scaledX,
            healthbarY,
            scaledY,
            healthPercent
          );
        }
      });
    });

    // Update healthbars for buildings
    Object.values(this.serverBuildings).forEach(buildings => {
      buildings.forEach(building => {
        const key = `b${building.playerId}_${building.id}`;
        currentObjectKeys.add(key);

        if (this.showHealthbars) {
          const scaledX = building.x / this.COORDINATE_SCALE;
          const scaledY = building.y / this.COORDINATE_SCALE;
          // Position healthbar above building using mesh's actual Y position
          const healthbarY = building.mesh.position.y + 1.5; // 1.5 units above the building

          // Get building's max health
          const maxHealth = BUILDING_DEFINITIONS[building.k]?.life || 100;
          const currentHealth = building.life ?? maxHealth;
          const healthPercent = Math.max(0, Math.min(1, currentHealth / maxHealth));

          this.createOrUpdateHealthbar(
            key,
            scaledX,
            healthbarY,
            scaledY,
            healthPercent
          );
        }
      });
    });

    // Remove healthbars if toggle is off or object no longer exists
    Object.keys(this.healthbars).forEach(key => {
      if (!this.showHealthbars || !currentObjectKeys.has(key)) {
        const healthbar = this.healthbars[key];
        if (healthbar) {
          healthbar.mesh.dispose();
          healthbar.texture.dispose();
          delete this.healthbars[key];
        }
      }
    });
  }

  /**
   * Create or update a healthbar above a unit or building
   */
  private createOrUpdateHealthbar(
    key: string,
    x: number,
    y: number,
    z: number,
    healthPercent: number
  ): void {
    let healthbar = this.healthbars[key];

    if (!healthbar) {
      // Create healthbar plane
      const healthbarPlane = MeshBuilder.CreatePlane(
        `healthbar_${key}`,
        { width: 0.8, height: 0.1 },
        this.scene
      );
      healthbarPlane.billboardMode = Mesh.BILLBOARDMODE_ALL; // Always face camera
      healthbarPlane.position = new Vector3(x, y, z);

      // Create dynamic texture for healthbar
      const textureSize = 128;
      const texture = new DynamicTexture(
        `healthbarTexture_${key}`,
        { width: textureSize, height: textureSize },
        this.scene,
        false
      );

      // Create material
      const material = new StandardMaterial(`healthbarMaterial_${key}`, this.scene);
      material.diffuseTexture = texture;
      material.emissiveTexture = texture;
      material.disableLighting = true;
      material.backFaceCulling = false;
      healthbarPlane.material = material;

      healthbar = { mesh: healthbarPlane, texture };
      this.healthbars[key] = healthbar;
    }

    // Update position
    healthbar.mesh.position = new Vector3(x, y, z);

    // Update texture with healthbar visualization
    this.drawHealthbarTexture(healthbar.texture, healthPercent);

    healthbar.mesh.isVisible = this.showHealthbars;
  }

  /**
   * Draw healthbar on dynamic texture
   */
  private drawHealthbarTexture(texture: DynamicTexture, healthPercent: number): void {
    const ctx = texture.getContext();
    const width = texture.getSize().width;
    const height = texture.getSize().height;

    // Clear canvas
    ctx.clearRect(0, 0, width, height);

    // Draw background (dark red/black)
    ctx.fillStyle = '#330000';
    ctx.fillRect(0, 0, width, height);

    // Draw border
    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = 2;
    ctx.strokeRect(1, 1, width - 2, height - 2);

    // Draw health fill
    const fillWidth = Math.floor((width - 4) * healthPercent);
    if (fillWidth > 0) {
      // Color gradient: green -> yellow -> red
      let fillColor: string;
      if (healthPercent > 0.6) {
        fillColor = '#00ff00'; // Green
      } else if (healthPercent > 0.3) {
        fillColor = '#ffff00'; // Yellow
      } else {
        fillColor = '#ff0000'; // Red
      }

      ctx.fillStyle = fillColor;
      ctx.fillRect(2, 2, fillWidth, height - 4);
    }

    texture.update();
  }

  /**
   * Create or update a sector label billboard
   */
  private createSectorLabel(
    key: string,
    x: number,
    y: number,
    z: number,
    labelText: string
  ): void {
    let label = this.sectorLabels[key];

    if (!label) {
      // Create label plane
      const labelPlane = MeshBuilder.CreatePlane(
        `sectorLabel_${key}`,
        { width: 20, height: 10 },
        this.scene
      );
      labelPlane.billboardMode = Mesh.BILLBOARDMODE_ALL; // Always face camera
      labelPlane.position = new Vector3(x, y, z); // Position above sector center

      // Create dynamic texture for label
      const textureSize = 256;
      const texture = new DynamicTexture(
        `sectorLabelTexture_${key}`,
        { width: textureSize, height: textureSize },
        this.scene,
        false
      );

      // Create material
      const material = new StandardMaterial(`sectorLabelMaterial_${key}`, this.scene);
      material.diffuseTexture = texture;
      material.emissiveTexture = texture;
      material.disableLighting = true;
      material.backFaceCulling = false;
      labelPlane.material = material;

      label = { mesh: labelPlane, texture };
      this.sectorLabels[key] = label;
    }

    // Update position
    label.mesh.position = new Vector3(x, y + 5, z);

    // Update texture with label text
    this.drawSectorLabelTexture(label.texture, labelText);

    label.mesh.isVisible = true;
  }

  /**
   * Draw sector label text on dynamic texture
   */
  private drawSectorLabelTexture(texture: DynamicTexture, labelText: string): void {
    const ctx = texture.getContext() as CanvasRenderingContext2D;
    const width = texture.getSize().width;
    const height = texture.getSize().height;

    // Clear canvas
    ctx.clearRect(0, 0, width, height);

    // Set text style
    ctx.font = 'bold 120px Arial';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillStyle = '#ffffff';
    ctx.strokeStyle = '#000000';
    ctx.lineWidth = 8;

    // Draw text with outline
    ctx.strokeText(labelText, width / 2, height / 2);
    ctx.fillText(labelText, width / 2, height / 2);

    texture.update();
  }

  private updateStars(): void {
    // Star is always at 100, 100, 100 relative to the player camera
    // But as the camera gets closer to 100, 100, 100, do allow the star to get closer...
    const dx = 500 + this.camera.position.x * 0.9;
    const dy = 500 + this.camera.position.y * 0.9;
    const dz = 500 + this.camera.position.z * 0.9;

    let now = Date.now();

    this.star.position.x = dx;
    this.star.position.y = dy;
    this.star.position.z = dz;
    this.stars.forEach((star, index) => {
      const nowish = now / 1000;
      const indexIsh = index / 10;
      let angle = nowish + indexIsh;
      let sinBit = Math.sin(angle);
      let cosBit = Math.cos(angle);
      star.position.x = dx + 3 * cosBit;
      star.position.y = dy + 3 * sinBit;
      star.position.z = dz + 3 * cosBit;
    });

    const backgroundStarDistance = 1000;
    this.backgroundStars.forEach((star, index) => {
      const direction = this.backgroundStarDirections[index];
      const offset = direction.scale(backgroundStarDistance);
      star.position.x = this.camera.position.x + offset.x;
      star.position.y = this.camera.position.y + offset.y;
      star.position.z = this.camera.position.z + offset.z;
    });
  }

  /**
   * Transforms world coordinates to sector A1's local 3D position
   * @param worldX World X coordinate (0-10000)
   * @param worldY World Y coordinate (0-10000)
   * @param heightOffset Height offset from sector surface (default: 0)
   * @returns Vector3 position in sector A1's coordinate system
   */
  private worldToSectorA1Position(worldX: number, worldY: number, heightOffset: number = 0): Vector3 {
    return worldToSectorA1(
      worldX,
      worldY,
      this.sectorA1Center,
      this.sectorA1Right,
      this.sectorA1Up,
      this.sectorA1Normal,
      this.COORDINATE_SCALE,
      this.WORLD_SIZE,
      heightOffset
    );
  }

  private alignBuildingToPlanet(mesh: Mesh | InstancedMesh): void {
    const planetCentre = this.planet.position;
    mesh.lookAt(planetCentre, 0, Math.PI / 2, 0);
  }

  /**
   * Calculates rotation angle in sector A1's local coordinate space
   * @param worldDx World X delta (velocity or direction)
   * @param worldDy World Y delta (velocity or direction)
   * @returns Rotation angle in radians around the sector's normal axis
   */
  private calculateSectorA1Rotation(worldDx: number, worldDy: number): number {
    // Convert world direction to sector A1 local direction
    // World X maps to sector right, World Y maps to sector up
    const localDx = worldDx / this.COORDINATE_SCALE;
    const localDz = worldDy / this.COORDINATE_SCALE;
    // Calculate angle in the sector's local X-Z plane
    return Math.atan2(localDx, localDz);
  }

  /**
   * Create or update a guard position line from unit to guard position
   */
  private createOrUpdateGuardLine(
    key: string,
    unitX: number,
    unitY: number,
    guardX: number,
    guardY: number,
    color: Color3
  ): void {
    let line = this.guardPositionLines[key];
    const points = [
      this.worldToSectorA1Position(unitX, unitY, 0.2),
      this.worldToSectorA1Position(guardX, guardY, 0.2)
    ];

    if (!line) {
      // Create new line
      line = MeshBuilder.CreateLines(
        `guardLine_${key}`,
        { points, updatable: true },
        this.scene
      );
      (line as any).color = color;
      this.guardPositionLines[key] = line;
    } else {
      // Update existing line
      MeshBuilder.CreateLines(null, { points, instance: line as any, updatable: true });
      (line as any).color = color;
    }
    line.isVisible = true;
  }

  private createScene(): Scene {
    const scene = new Scene(this.engine);

    // Camera setup - changed to FreeCamera
    this.camera = new FreeCamera(
      "camera",
      new Vector3(0, 100, 0),
      scene
    );
    this.camera.setTarget(Vector3.Zero());

    // Set some reasonable movement boundaries
    this.camera.minZ = 0.1;
    this.camera.maxZ = 10000;

    // Adjust camera speed
    this.camera.speed = 1.0;
    this.camera.angularSensibility = 1000;

    // Initialize camera in orbit mode (default)
    // Note: We'll initialize orbit position after planet is created
    // For now, just set the mode - orbit camera will be initialized later
    if (this.cameraMode === 'orbit') {
      // Don't attach controls yet - will be done after planet is created
    } else {
      this.camera.attachControl(this.canvas, true);
    }

    // Add keyboard controls
    scene.onKeyboardObservable.add((kbInfo) => {
      switch (kbInfo.type) {
        case KeyboardEventTypes.KEYDOWN:
          switch (kbInfo.event.key.toLowerCase()) {
            case 'w':
              this.keyState.w = true;
              break;
            case 's':
              this.keyState.s = true;
              break;
            case 'a':
              this.keyState.a = true;
              break;
            case 'd':
              this.keyState.d = true;
              break;
            case 'x':
              this.keyState.x = true;
              break;
            case 'c':
              this.keyState.c = true;
              break;
            case 'm':
              // M key - toggle space layer (map view)
              this.toggleSpaceLayer();
              break;
          }
          break;
        case KeyboardEventTypes.KEYUP:
          switch (kbInfo.event.key.toLowerCase()) {
            case 'w':
              this.keyState.w = false;
              break;
            case 's':
              this.keyState.s = false;
              break;
            case 'a':
              this.keyState.a = false;
              break;
            case 'd':
              this.keyState.d = false;
              break;
            case 'x':
              this.keyState.x = false;
              break;
            case 'c':
              this.keyState.c = false;
              break;
          }
          break;
      }
    });

    // Lighting
    const light = new HemisphericLight(
      "light",
      new Vector3(0.2, 1, 0.2),
      scene
    );
    light.intensity = 1;
    light.specular = new Color3(0, 0, 0);
    light.diffuse = new Color3(1, 1, 1);
    light.groundColor = new Color3(0, 0, 0);

    // Put long rectangles from origin in the direction of x, y, and z so we can orient ourselves better
    const xAxisHelper = MeshBuilder.CreateBox("xRect", { width: 10, height: 5, depth: 0.1 }, scene);
    xAxisHelper.position = new Vector3(this.WORLD_SIZE / this.COORDINATE_SCALE / 2, 0, 0);
    const yAxisHelper = MeshBuilder.CreateBox("yRect", { width: 5, height: 10, depth: 0.1 }, scene);
    yAxisHelper.position = new Vector3(5, 5 + this.WORLD_SIZE / this.COORDINATE_SCALE / 2, 0);
    const zAxisHelper = MeshBuilder.CreateBox("zRect", { width: 0.1, height: 5, depth: 10 }, scene);
    zAxisHelper.position = new Vector3(0, 0, this.WORLD_SIZE / this.COORDINATE_SCALE / 2);
    const xRectMaterial = new StandardMaterial("xRectMaterial", scene);
    xRectMaterial.diffuseColor = new Color3(1, 0, 0);
    xAxisHelper.material = xRectMaterial;
    const yRectMaterial = new StandardMaterial("yRectMaterial", scene);
    yRectMaterial.diffuseColor = new Color3(0, 1, 0);
    yAxisHelper.material = yRectMaterial;
    const zRectMaterial = new StandardMaterial("zRectMaterial", scene);
    zRectMaterial.diffuseColor = new Color3(0, 0, 1);
    zAxisHelper.material = zRectMaterial;

    // Ground is the world divided into a 8x8 grid with subtle colour variation
    const GRID_SUBDIVISIONS = 8;
    const groundMesh = MeshBuilder.CreateGround(
      "ground",
      { width: this.WORLD_SIZE / this.COORDINATE_SCALE / GRID_SUBDIVISIONS, height: this.WORLD_SIZE / this.COORDINATE_SCALE / GRID_SUBDIVISIONS },
      scene
    );

    const halfWidth = (this.WORLD_SIZE / this.COORDINATE_SCALE) / 2;
    const fullWidth = this.WORLD_SIZE / this.COORDINATE_SCALE;

    const hsvToRgb = (h: number, s: number, v: number) => {
      return Color3.FromHSV(h, s, v);
    }

    const halfGridOffset = this.WORLD_SIZE / this.COORDINATE_SCALE / GRID_SUBDIVISIONS / 2;

    for (let i = 0; i < GRID_SUBDIVISIONS; i++) {
      for (let j = 0; j < GRID_SUBDIVISIONS; j++) {
        const x = i * (this.WORLD_SIZE / this.COORDINATE_SCALE / GRID_SUBDIVISIONS) + halfGridOffset;
        const z = j * (this.WORLD_SIZE / this.COORDINATE_SCALE / GRID_SUBDIVISIONS) + halfGridOffset;
        // Use a clone so we can set the material to a different colour
        const groundClone = groundMesh.clone("groundInstance_" + i + "_" + j);
        groundClone.position = new Vector3(x, 0, z);
        const groundMaterial = new StandardMaterial("groundMat_" + i + "_" + j, scene);
        const hsvColour = hsvToRgb(360 * (0.15 + 0.2 * i / GRID_SUBDIVISIONS), 0.3 + 0.2 * j / GRID_SUBDIVISIONS, 0.3);
        groundMaterial.diffuseColor = hsvColour;
        groundClone.material = groundMaterial;
      }
    }

    // Remove the ground mesh, thank you for the help
    groundMesh.dispose();

    const satelliteMesh = MeshBuilder.CreateGround(
      "satelliteMesh",
      { width: 30, height: 30 },
      scene
    );
    const satelliteMaterial = new StandardMaterial("satelliteMaterial", scene);
    satelliteMaterial.diffuseColor = new Color3(0.1, 0.8, 0.8);
    satelliteMaterial.backFaceCulling = false;
    satelliteMaterial.alpha = 0.5;
    satelliteMesh.material = satelliteMaterial;
    satelliteMesh.position = new Vector3(halfWidth, 0, halfWidth + this.WORLD_SIZE / this.COORDINATE_SCALE + 50);

    const wallHeight = 5;

    const steepWallMaterial = new StandardMaterial("steepWallMaterial", scene);
    steepWallMaterial.diffuseColor = new Color3(0.7, 0.5, 0.7);

    // Aronnd the ground are steep walls, so we can't escape
    const steepWallHorizontalWest = MeshBuilder.CreateBox(
      "wall",
      { width: this.WORLD_SIZE / this.COORDINATE_SCALE, height: wallHeight, depth: 1 },
      scene
    );
    steepWallHorizontalWest.material = steepWallMaterial;
    const steepWallHorizontalEast = steepWallHorizontalWest.clone("steepWallHorizontalEast");
    const steepWallVerticalNorth = steepWallHorizontalWest.clone("steepWallVerticalNorth");
    const steepWallVerticalSouth = steepWallVerticalNorth.clone("steepWallVerticalSouth");

    steepWallHorizontalWest.rotation = new Vector3(-Math.PI / 4, 0, 0);
    steepWallHorizontalWest.position = new Vector3(halfWidth, wallHeight / 2 - 1, 0);

    steepWallHorizontalEast.rotation = new Vector3(Math.PI / 4, 0, 0);
    steepWallHorizontalEast.position = new Vector3(halfWidth, wallHeight / 2 - 1, fullWidth);

    steepWallVerticalNorth.rotation = new Vector3(-Math.PI / 4, Math.PI / 2, 0);
    steepWallVerticalNorth.position = new Vector3(0, wallHeight / 2 - 1, halfWidth);

    steepWallVerticalSouth.rotation = new Vector3(-Math.PI / 4, -Math.PI / 2, 0);
    steepWallVerticalSouth.position = new Vector3(fullWidth, wallHeight / 2 - 1, halfWidth);

    // Add a giant sphere far below us, i.e., we're on a planet
    // Tile "most" of the sphere with squares (arenas) of the same size
    // Strategy: Use latitude bands (rings at different Y offsets), excluding polar regions

    const ARENA_SIZE = 10000; // Game coordinates (10000x10000)
    const ARENA_SIZE_VISUAL = ARENA_SIZE / this.COORDINATE_SCALE; // Visual size (200x200)

    // Planet center and dimensions
    const PLANET_CENTER_X = halfWidth;
    const PLANET_CENTER_Y = -500;
    const PLANET_CENTER_Z = halfWidth;

    const TARGET_SQUARES_PER_EQUATOR = 16;
    const EQUATOR_CIRCUMFERENCE = TARGET_SQUARES_PER_EQUATOR * ARENA_SIZE_VISUAL;
    const REQUIRED_EQUATOR_RADIUS = EQUATOR_CIRCUMFERENCE / (2 * Math.PI);
    let OPTIMAL_PLANET_DIAMETER = REQUIRED_EQUATOR_RADIUS * 2;

    // Let's just make it a power of two for now, humans like that...
    OPTIMAL_PLANET_DIAMETER = 1024;

    const PLANET_DIAMETER = Math.round(OPTIMAL_PLANET_DIAMETER);
    const PLANET_RADIUS = PLANET_DIAMETER / 2;

    // Polar exclusion: don't place squares too close to the poles
    const POLAR_EXCLUSION_ANGLE = Math.PI / 5;
    const MAX_Y_OFFSET = PLANET_RADIUS * Math.cos(POLAR_EXCLUSION_ANGLE);
    const MIN_BAND_RADIUS = ARENA_SIZE_VISUAL * 0.5; // Don't place bands where radius is too small

    console.log(`=== Sphere Tiling Calculation ===`);
    console.log(`Arena size: ${ARENA_SIZE}x${ARENA_SIZE} (game) = ${ARENA_SIZE_VISUAL}x${ARENA_SIZE_VISUAL} (visual)`);
    console.log(`Planet diameter: ${PLANET_DIAMETER} (radius: ${PLANET_RADIUS.toFixed(2)})`);
    console.log(`Target squares at equator: ${TARGET_SQUARES_PER_EQUATOR}`);
    console.log(`Equator circumference: ${(2 * Math.PI * PLANET_RADIUS).toFixed(2)}`);
    console.log(`Polar exclusion: ±${(POLAR_EXCLUSION_ANGLE * 180 / Math.PI).toFixed(1)} degrees`);
    console.log(`Max Y offset from center: ${MAX_Y_OFFSET.toFixed(2)}`);

    // Create planet
    const planet = MeshBuilder.CreateSphere("planet", { diameter: PLANET_DIAMETER }, scene);
    this.planet = planet;
    planet.position = new Vector3(PLANET_CENTER_X, PLANET_CENTER_Y, PLANET_CENTER_Z);
    const planetMaterial = new StandardMaterial("planetMaterial", scene);
    planetMaterial.diffuseColor = new Color3(0.5, 0.5, 0.5);
    planetMaterial.alpha = 0;
    planet.material = planetMaterial;

    // Create low - poly terrain
    const terrain = createLowPolyTerrain(
      scene,
      new Vector3(PLANET_CENTER_X, PLANET_CENTER_Y, PLANET_CENTER_Z),
      PLANET_RADIUS
    );

    // Create sectors/arenas in latitude bands
    const sectorMaterial = new StandardMaterial("sectorMaterial", scene);
    sectorMaterial.diffuseColor = new Color3(0.2, 0.6, 0.8);
    sectorMaterial.alpha = 0.3;
    sectorMaterial.emissiveColor = new Color3(0.1, 0.3, 0.4);

    const outlineMaterial = new StandardMaterial("sectorOutlineMaterial", scene);
    outlineMaterial.diffuseColor = new Color3(0.3, 0.7, 1);
    outlineMaterial.wireframe = true;
    outlineMaterial.alpha = 0.3;
    outlineMaterial.emissiveColor = new Color3(0.2, 0.5, 0.7);

    // Calculate latitude bands
    // Each band is a circle in the X-Z plane at constant Y
    // At Y offset dy from center, the circle radius is: sqrt(R^2 - dy^2)
    const Y_STEP = ARENA_SIZE_VISUAL; // Step by one arena size
    const bands: Array<{ y: number, radius: number, count: number }> = [];


    // Start from negative Z and work our way up
    // for (let yOffset = MAX_Y_OFFSET; yOffset >= -MAX_Y_OFFSET; yOffset -= Y_STEP) {
    //   // Calculate the radius of the circle at this Y offset
    //   const bandRadius = Math.sqrt(PLANET_RADIUS * PLANET_RADIUS - yOffset * yOffset);

    //   // Skip if radius is too small
    //   if (bandRadius < MIN_BAND_RADIUS) continue;

    //   // Calculate how many squares fit around this band
    //   const circumference = 2 * Math.PI * bandRadius;
    //   const squaresInBand = Math.floor(circumference / ARENA_SIZE_VISUAL);

    //   if (squaresInBand < 1) continue; // Skip if we can't fit at least one

    //   bands.push({ y: yOffset, radius: bandRadius, count: squaresInBand });
    // }


    const desired = [8, 12, 16, 12, 8];
    for (let i = 0; i < desired.length; i++) {
      const yOffset = MAX_Y_OFFSET - i * Y_STEP;
      const bandRadius = Math.sqrt(PLANET_RADIUS * PLANET_RADIUS - yOffset * yOffset);
      const count = desired[i];
      bands.push({ y: yOffset, radius: bandRadius, count: count });
    }

    let totalSectors = bands.reduce((sum, band) => sum + band.count, 0);

    console.log(`\nLatitude bands: ${bands.length}`);
    console.log(`Total sectors: ${totalSectors}`);
    bands.forEach((band, i) => {
      console.log(`  Band ${i}: y=${band.y.toFixed(1)}, radius=${band.radius.toFixed(1)}, squares=${band.count}`);
    });
    console.log(`=================================`);

    // Create sectors for each band
    let sectorIndex = 0;
    bands.forEach((band, bandIndex) => {
      const y = PLANET_CENTER_Y + band.y;
      const angleStep = (2 * Math.PI) / band.count;

      for (let i = 0; i < band.count; i++) {
        const angle = i * angleStep;
        const helperAngle = angle;

        // Position on the circle in X-Y plane at this Z
        const centerX = PLANET_CENTER_X + Math.cos(angle) * band.radius;
        const centerY = y;
        const centerZ = PLANET_CENTER_Z + Math.sin(angle) * band.radius;

        const helperX = PLANET_CENTER_X + Math.cos(helperAngle) * band.radius;
        const helperY = y;
        const helperZ = PLANET_CENTER_Z + Math.sin(helperAngle) * band.radius;

        // Create sector box
        const sector = MeshBuilder.CreateBox(
          `futureSector_${sectorIndex}`,
          {
            width: ARENA_SIZE_VISUAL * 0.8,
            height: 0.1,
            depth: ARENA_SIZE_VISUAL * 0.8
          },
          scene
        );

        const planetCentre = new Vector3(PLANET_CENTER_X, PLANET_CENTER_Y, PLANET_CENTER_Z);
        const sectorCentre = new Vector3(centerX, centerY, centerZ);
        const helperCentre = new Vector3(helperX, helperY, helperZ);
        sector.position = new Vector3(centerX, centerY, centerZ);
        sector.lookAt(planetCentre, 0, Math.PI / 2, 0);

        sector.material = sectorMaterial;

        // Add wireframe outline
        const sectorOutline = MeshBuilder.CreateBox(
          `futureSectorOutline_${sectorIndex}`,
          {
            width: ARENA_SIZE_VISUAL * 0.8,
            height: 0.1,
            depth: ARENA_SIZE_VISUAL * 0.8
          },
          scene
        );

        sectorOutline.position = sectorCentre;
        sectorOutline.lookAt(planetCentre, 0, Math.PI / 2, 0);
        sectorOutline.material = outlineMaterial;

        // Add our traditional x, y, z, helpers to the sector
        const xAxisHelperClone = xAxisHelper.clone("xAxisHelperClone_" + sectorIndex);
        xAxisHelperClone.position = helperCentre;
        xAxisHelperClone.material = xRectMaterial;
        const yAxisHelperClone = yAxisHelper.clone("yAxisHelperClone_" + sectorIndex);
        yAxisHelperClone.position = helperCentre;
        yAxisHelperClone.material = yRectMaterial;
        const zAxisHelperClone = zAxisHelper.clone("zAxisHelperClone_" + sectorIndex);
        zAxisHelperClone.position = helperCentre;
        zAxisHelperClone.material = zRectMaterial;

        const dx = planetCentre.x - helperCentre.x;
        const dy = planetCentre.y - helperCentre.y;
        const dz = planetCentre.z - helperCentre.z;

        // And also derive those three angles from the sector centre to the planet centre

        // Note this... careful!
        const pitchCorrection = Math.PI / 2;

        const yaw = -Math.atan2(dz, dx) + Math.PI / 2;
        const len = Math.sqrt(dx * dx + dz * dz);
        const pitch = -Math.atan2(dy, len) + pitchCorrection;

        const overallRotation = new Vector3(pitch, yaw, 0);

        const centerVector = new Vector3(dx, dy, dz);

        const existingRotation = centerVector.clone().normalize();
        let upIsh = new Vector3(0, 1, 0);
        if (Math.abs(upIsh.dot(existingRotation)) > 0.99) {
          upIsh = new Vector3(0, 0, 1);
        }
        const right = upIsh.cross(existingRotation).normalize();
        const up = existingRotation.cross(right).normalize();
        const newXPosition = helperCentre.add(up.scale(-80)).add(right.scale(-75));
        const newYPosition = helperCentre.add(existingRotation.scale(-7.5)).add(right.scale(-77.5)).add(up.scale(-80));
        const newZPosition = helperCentre.add(right.scale(-80)).add(up.scale(-75));
        xAxisHelperClone.position = newXPosition;
        yAxisHelperClone.position = newYPosition;
        zAxisHelperClone.position = newZPosition;
        xAxisHelperClone.rotation = overallRotation;
        yAxisHelperClone.rotation = overallRotation;
        zAxisHelperClone.rotation = overallRotation;

        // Store sector A1 coordinate system (bandIndex=0, i=0)
        if (bandIndex === 0 && i === 0) {
          this.sectorA1Center = sectorCentre.clone();
          this.sectorA1Normal = existingRotation.clone(); // Points toward planet center
          this.sectorA1Right = right.clone();
          this.sectorA1Up = up.clone();
          console.log(`Sector A1 coordinate system stored:`);
          console.log(`  Center: (${this.sectorA1Center.x.toFixed(2)}, ${this.sectorA1Center.y.toFixed(2)}, ${this.sectorA1Center.z.toFixed(2)})`);
          console.log(`  Normal: (${this.sectorA1Normal.x.toFixed(2)}, ${this.sectorA1Normal.y.toFixed(2)}, ${this.sectorA1Normal.z.toFixed(2)})`);
          console.log(`  Right: (${this.sectorA1Right.x.toFixed(2)}, ${this.sectorA1Right.y.toFixed(2)}, ${this.sectorA1Right.z.toFixed(2)})`);
          console.log(`  Up: (${this.sectorA1Up.x.toFixed(2)}, ${this.sectorA1Up.y.toFixed(2)}, ${this.sectorA1Up.z.toFixed(2)})`);
        }

        // Create sector label
        const labelPosition = new Vector3(centerX, centerY, centerZ).add(existingRotation.scale(-50));
        const bandLetter = String.fromCharCode(65 + bandIndex); // A, B, C, D, E
        const sectorNumber = i + 1; // 1, 2, 3...
        const labelText = `${bandLetter}${sectorNumber}`;
        this.createSectorLabel(`sector_${bandIndex}_${i}`, labelPosition.x, labelPosition.y, labelPosition.z, labelText);

        sectorIndex++;
      }
    });

    // Add the orbital station
    const orbitalStation = MeshBuilder.CreateSphere("orbitalStation", { diameter: 100 }, scene);
    orbitalStation.position = new Vector3(200, 500, 200);
    const orbitalStationMaterial = new StandardMaterial("orbitalStationMaterial", scene);
    orbitalStationMaterial.diffuseColor = new Color3(0.5, 0.5, 0.5);
    orbitalStation.material = orbitalStationMaterial;

    // Add space ships (big rectangles) in the space layer
    const SPACE_LAYER_HEIGHT = 1000;
    const shipMaterial = new StandardMaterial("shipMaterial", scene);
    shipMaterial.diffuseColor = new Color3(0.3, 0.3, 0.7);
    shipMaterial.emissiveColor = new Color3(0.1, 0.1, 0.3);

    // Planet center (in scaled coordinates)
    const planetCenterX = halfWidth;
    const planetCenterY = -500;
    const planetCenterZ = halfWidth;

    // Create a few ships with orbital mechanics
    // Each ship will orbit at different radii and speeds
    const shipConfigs = [
      { radius: 600, orbitalSpeed: 0.0005, initialAngle: 0 },
      { radius: 650, orbitalSpeed: 0.0004, initialAngle: Math.PI / 3 },
      { radius: 700, orbitalSpeed: 0.0003, initialAngle: Math.PI * 2 / 3 },
      { radius: 750, orbitalSpeed: 0.00035, initialAngle: Math.PI },
      { radius: 800, orbitalSpeed: 0.00025, initialAngle: Math.PI * 4 / 3 }
    ];

    shipConfigs.forEach((config, index) => {
      const ship = MeshBuilder.CreateBox(
        `spaceShip_${index}`,
        { width: 80, height: 20, depth: 120 },
        scene
      );

      // Calculate initial position based on orbital angle
      const initialX = planetCenterX + Math.cos(config.initialAngle) * config.radius;
      const initialZ = planetCenterZ + Math.sin(config.initialAngle) * config.radius;

      ship.position = new Vector3(initialX, SPACE_LAYER_HEIGHT, initialZ);
      ship.material = shipMaterial;

      // Store orbital data
      this.spaceShips.push({
        mesh: ship,
        angle: config.initialAngle,
        radius: config.radius,
        orbitalSpeed: config.orbitalSpeed,
        centerX: planetCenterX,
        centerY: planetCenterY,
        centerZ: planetCenterZ
      });
    });

    // Add the star
    const star = MeshBuilder.CreateSphere("star", { diameter: 100 }, scene);
    star.position = new Vector3(500, 500, 500);
    const starMaterial = new StandardMaterial("starMaterial", scene);
    star.material = starMaterial;
    starMaterial.emissiveColor = new Color3(0.6, 0.6, 1);
    starMaterial.alpha = 0.1;
    starMaterial.disableLighting = true;
    this.star = star;

    // Add a few more stars with slight offsets so that the alpha stacks to make a nice glow
    const starCount = 9;
    for (let i = 0; i < starCount; i++) {
      const star = MeshBuilder.CreateSphere("star_" + i, { diameter: 100 + i * 3 }, scene);
      star.position = new Vector3(500, 500, 500);
      star.material = starMaterial;
      this.stars.push(star);
    }

    // Add background stars that are always visible but unreachable;
    const backgroundStarCount = 50;
    const backgroundStarDistance = 1000;
    const backgroundStarMaterial = new StandardMaterial("backgroundStarMaterial", scene);
    backgroundStarMaterial.emissiveColor = new Color3(0.8, 0.8, 1);
    backgroundStarMaterial.alpha = 0.8;
    backgroundStarMaterial.disableLighting = true;

    let seed = 54321;
    const seededRandom = () => {
      seed = (seed * 9301 + 49297) % 233280;
      return seed / 233280;
    };
    const seededRandomFloat = (min: number, max: number) => min + (max - min) * seededRandom();

    for (let i = 0; i < backgroundStarCount; i++) {
      const theta = seededRandomFloat(0, Math.PI * 2);
      const phi = Math.acos(seededRandomFloat(-1, 1));
      const direction = new Vector3(
        Math.sin(phi) * Math.cos(theta),
        Math.sin(phi) * Math.sin(theta),
        Math.cos(phi)
      );

      const starSize = seededRandomFloat(3, 10);
      const backgroundStar = MeshBuilder.CreateSphere("backgroundStar_" + i, { diameter: starSize }, scene);
      backgroundStar.position = direction.scale(backgroundStarDistance);
      backgroundStar.material = backgroundStarMaterial;
      this.backgroundStars.push(backgroundStar);
      this.backgroundStarDirections.push(direction.clone());
    }

    let angle = 0;
    const origin = planet.position;
    const radius = 900;
    const detail = 20;
    for (let i = 0; i < detail; i++) {
      angle = i * 2 * Math.PI / detail;
      const satelliteClone = satelliteMesh.clone("satelliteClone_" + i);
      satelliteClone.material = satelliteMaterial;
      satelliteClone.position = new Vector3(origin.x + Math.cos(angle) * radius, origin.y + Math.sin(angle) * radius - 5, halfWidth);
      satelliteClone.rotation = new Vector3(0, 0, angle - Math.PI / 2);
      this.satellites.push(satelliteClone);
    }

    // Initialize camera based on mode
    if (this.cameraMode === 'orbit') {
      // Initialize orbit camera (detach mouse controls, set position)
      this.camera.detachControl();
      this.initializeOrbitCamera();
    } else {
      // Free camera mode - attach mouse controls
      this.camera.attachControl(this.canvas, true);
      // Look at center
      this.camera.setTarget(new Vector3(halfWidth, 0, halfWidth));
    }

    return scene;
  }

  /**
   * Start quadtree visualization updates
   */
  private startQuadtreeVisualization(): void {
    // Fetch quadtree data immediately, then every 10 seconds
    this.updateQuadtreeVisualization();
    this.quadtreeUpdateInterval = window.setInterval(() => {
      this.updateQuadtreeVisualization();
    }, 10000); // 10 seconds
  }

  /**
   * Fetch quadtree data from server and update visualization
   */
  private async updateQuadtreeVisualization(): Promise<void> {
    try {
      const response = await fetch('/api/quadtrees');
      if (!response.ok) {
        console.warn('Failed to fetch quadtree data');
        return;
      }
      const quadtrees = await response.json();
      this.renderQuadtrees(quadtrees);
    } catch (error) {
      console.warn('Error fetching quadtree data:', error);
    }
  }

  /**
   * Render quadtrees as outlined rectangles with different colors and Y offsets
   */
  private renderQuadtrees(quadtrees: {
    units: Array<{ x: number, y: number, width: number, height: number, depth: number, entityCount: number }>;
    buildings: Array<{ x: number, y: number, width: number, height: number, depth: number, entityCount: number }>;
    ores: Array<{ x: number, y: number, width: number, height: number, depth: number, entityCount: number }>;
  }): void {
    // Clear existing meshes
    this.clearQuadtreeMeshes();

    // Colors for each quadtree type
    const colors = {
      units: new Color3(1, 0, 0),      // Red
      buildings: new Color3(0, 1, 0),   // Green
      ores: new Color3(0, 0, 1)         // Blue
    };

    // Y offsets to separate the quadtrees visually (in world coordinates, will be scaled)
    const yOffsets = {
      units: 0.5,      // Slightly above ground
      buildings: 1.0,  // Higher
      ores: 1.5        // Highest
    };

    // Render each quadtree type
    this.renderQuadtreeType('units', quadtrees.units, colors.units, yOffsets.units);
    this.renderQuadtreeType('buildings', quadtrees.buildings, colors.buildings, yOffsets.buildings);
    this.renderQuadtreeType('ores', quadtrees.ores, colors.ores, yOffsets.ores);
  }

  /**
   * Render a specific quadtree type
   */
  private renderQuadtreeType(
    type: 'units' | 'buildings' | 'ores',
    nodes: Array<{ x: number, y: number, width: number, height: number, depth: number, entityCount: number }>,
    color: Color3,
    yOffset: number
  ): void {
    nodes.forEach((node, index) => {
      // Convert world coordinates to 3D coordinates
      const x = node.x / this.COORDINATE_SCALE;
      const y = yOffset;
      const z = node.y / this.COORDINATE_SCALE;
      const width = node.width / this.COORDINATE_SCALE;
      const height = node.height / this.COORDINATE_SCALE;

      // Create rectangle outline using lines
      // Define the 4 corners of the rectangle
      const points = [
        new Vector3(x, y, z),                                    // Top-left
        new Vector3(x + width, y, z),                            // Top-right
        new Vector3(x + width, y, z + height),                   // Bottom-right
        new Vector3(x, y, z + height),                          // Bottom-left
        new Vector3(x, y, z)                                    // Close the rectangle
      ];

      // Create lines mesh
      const line = MeshBuilder.CreateLines(
        `quadtree_${type}_${index}`,
        { points: points },
        this.scene
      );

      // Set color
      line.color = color;

      // Store reference for cleanup
      this.quadtreeMeshes[type].push(line);
    });
  }

  /**
   * Clear all quadtree visualization meshes
   */
  private clearQuadtreeMeshes(): void {
    Object.keys(this.quadtreeMeshes).forEach(type => {
      this.quadtreeMeshes[type as keyof typeof this.quadtreeMeshes].forEach(mesh => {
        mesh.dispose();
      });
      this.quadtreeMeshes[type as keyof typeof this.quadtreeMeshes] = [];
    });
  }

  private saveCode(): void {
    const file = document.getElementById('file') as HTMLInputElement;
    file.click();
  }

  private saveCodeToServer(code: string): void {
    fetch('/api/code', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        code,
        playerId: this.worldPlayerId
      })
    }).then(() => {
      console.log('Code saved');
      // Clean up 'file' input
      const file = document.getElementById('file') as HTMLInputElement;
      file.value = '';
    }).catch(() => {
      console.error('Failed to save code');
    });
  }

  private hasNoHubBuildingsOrUnits(): boolean {
    if (this.worldPlayerId < 0) {
      return false;
    }
    const myBuildings = this.serverBuildings[this.worldPlayerId] || [];
    const myUnits = this.serverUnits[this.worldPlayerId] || [];

    // Check if there's a hub
    const hasHub = myBuildings.some(b => b.k === BuildingType.Hub);

    // Return true only if no hub, no buildings, and no units
    return !hasHub && myBuildings.length === 0 && myUnits.length === 0;
  }

  private updateRespawnButtonState(): void {
    const respawnButton = document.getElementById('respawn') as HTMLButtonElement;
    if (!respawnButton || this.worldPlayerId < 0) {
      return;
    }

    const canRespawn = this.hasNoHubBuildingsOrUnits();
    respawnButton.disabled = !canRespawn;

    // Update button appearance based on state
    if (canRespawn) {
      respawnButton.style.backgroundColor = '#0891b2'; // cyan-700
      respawnButton.style.opacity = '1';
      respawnButton.style.cursor = 'pointer';
    } else {
      respawnButton.style.backgroundColor = '#475569'; // slate-600 (grayed out)
      respawnButton.style.opacity = '0.6';
      respawnButton.style.cursor = 'not-allowed';
    }
  }

  private handleRespawn(): void {
    if (!this.hasNoHubBuildingsOrUnits()) {
      console.log('Cannot respawn: you have buildings or units');
      return;
    }

    // Get current code from editor or saved code
    let code = '';
    const aceEditor = (window as any).editor;
    if (aceEditor) {
      code = aceEditor.getValue() || '';
    }

    // If no code in editor, try to get saved code
    if (!code) {
      const savedCode = localStorage.getItem('overwhelmed-code');
      if (savedCode) {
        code = savedCode;
      }
    }

    // Send code to server (same effect as uploading code when you have nothing)
    this.saveCodeToServer(code);
  }

  /**
   * Queue a console message to be processed in the render loop
   * This batches console updates to align with the game's update cycle
   */
  public queueConsoleMessage(message: string, color?: string): void {
    this.consoleMessageQueue.push({ message, color });
  }

  /**
   * Process console message queue in the render loop
   * Batches multiple messages together for better performance
   */
  private updateConsole(): void {
    if (this.consoleMessageQueue.length === 0 && !this.consoleNeedsScroll) {
      return;
    }

    const gameConsole = document.getElementById('console');
    if (!gameConsole) return;

    // Check if user is near the bottom (within 50px) - if so, auto-scroll
    const isNearBottom = gameConsole.scrollHeight - gameConsole.scrollTop - gameConsole.clientHeight < 50;

    // Process all queued messages at once
    if (this.consoleMessageQueue.length > 0) {
      const fragment = document.createDocumentFragment();
      this.consoleMessageQueue.forEach(({ message, color }) => {
        const p = document.createElement('p');
        if (color) {
          p.style.color = color;
        }
        p.textContent = message;
        fragment.appendChild(p);
      });

      gameConsole.appendChild(fragment);
      this.consoleMessageQueue = [];

      // If height overflow, remove oldest messages
      while (gameConsole.scrollHeight > 10 * gameConsole.clientHeight) {
        const firstChild = gameConsole.firstChild;
        if (firstChild) {
          gameConsole.removeChild(firstChild);
        } else {
          break;
        }
      }

      // Mark that we need to scroll if user was near bottom
      if (isNearBottom) {
        this.consoleNeedsScroll = true;
      }
    }

    // Perform scroll if needed (after DOM updates)
    if (this.consoleNeedsScroll) {
      gameConsole.scrollTop = gameConsole.scrollHeight;
      this.consoleNeedsScroll = false;
    }
  }
}

// Start the game when the window loads
window.addEventListener('DOMContentLoaded', () => {
  new Game3D();
}); 